// THE GROUND PAINTER'S PIXEL CODE (APP_PREPAINT "Keys", APP_GENSURF §5e): the GLSL of every pass, the shape constants
// the uploads share with it, the static ways look and the genome record table — moved out of groundgen.js (the
// scheduler) so the painter hash (engine/steps/_prepaint/painter.mjs PAINTER) covers what makes pixels and nothing that
// only decides WHEN a key paints: a scheduling edit keeps every shipped slot a hit. Nothing here imports the scheduler
// (sched / gensched / gmtex / device; gate check-prepaint "painter key scope").
import { SLOT_PX, LIGHT, OPACITY, RES_PER_STOP, WHITE, M_INV, lab, TIN_EX, TIN_EZ, strataField, TILE } from './groundref.js';
import { prepWays, WAYS_K, XYZ_INV, Q4 } from './groundways.js';
import { genomeTexelConsts } from './genometex.js';
import { staticWays as staticWaysOf } from './groundcpu.js';
import PAL from './painterpal.json';
import WAYSLOOK from '../../pipeline/wayslook.json';
import { MARKS, PAINT } from '../../game/waysdraw.js';

export const NCMAX = 20;                 // compact classes (ids 0..15 + water + none + spare)
export const MAXB = 8;                   // ways: distinct look bytes under one texel's tent
export const MAXA = 4;                   // ways: distinct path bytes in one coarse texel's cells
export const RW = 48;                    // genome record table: vec4s per record row
export const PAL_MAX = 9;                // genome palette entries a record may carry
export const MARKS_IN = { line_m: MARKS.line_m, kerb_line_m: PAINT.kerb_line_m, ground: MARKS.ground };
export const roundEven = (v) => { const r = Math.round(v); return Math.abs(v % 1) === 0.5 && r % 2 !== 0 ? r - 1 : r; };
export const clamp = (v, lo, hi) => (v < lo ? lo : v > hi ? hi : v);
export const octaveKeep = (wl, texel) => { const t = clamp((wl / texel - 2.0) / 2.0, 0.0, 1.0); return t * t * (3 - 2 * t); };

// ---------------------------------------------------------------- GLSL text helpers
const fl = (v) => { const s = String(+v); return /[.e]/.test(s) ? s : s + '.0'; };
const v3 = (a) => `vec3(${fl(a[0])}, ${fl(a[1])}, ${fl(a[2])})`;
const v4 = (a) => `vec4(${fl(a[0])}, ${fl(a[1])}, ${fl(a[2])}, ${fl(a[3])})`;
const arr = (t, xs) => `${t}[${xs.length}](${xs.join(', ')})`;

const HEAD = `#version 300 es
precision highp float;
precision highp int;
precision highp sampler2D;
precision highp usampler2D;
`;
const VS_FULL = HEAD + `void main() {
  gl_Position = vec4(float((gl_VertexID & 1) << 2) - 1.0, float((gl_VertexID & 2) << 1) - 1.0, 0.0, 1.0);
}
`;

// the raster (groundref paintRect): texel (i, k) at local (i - m + 0.5) texel from the slot
// origin uX0 (integer m: every world-integer lattice is local-integer)
const RASTER = `
const float PI = 3.141592653589793;
uniform float uTexel;
uniform int uM;
uniform ivec2 uPW;
uniform ivec2 uX0;
vec2 dOf(ivec2 q) { return (vec2(q - uM) + 0.5) * uTexel; }
float Xr(int i) { return float(uX0.x) + (float(i - uM) + 0.5) * uTexel; }
float Zr(int k) { return float(uX0.y) + (float(k - uM) + 0.5) * uTexel; }
float smooth01(float t, float lo, float hi) { t = clamp((t - lo) / (hi - lo), 0.0, 1.0); return t * t * (3.0 - 2.0 * t); }
float rnd(float v) { return floor(v + 0.5); }
float octKeep(float wl) { float t = clamp((wl / uTexel - 2.0) / 2.0, 0.0, 1.0); return t * t * (3.0 - 2.0 * t); }
float lum(vec3 c) { return 0.299 * c.r + 0.587 * c.g + 0.114 * c.b; }
`;

// surfacegen _gnoise / groundways gnoise / genometex gnoise32: the uint32 lattice hash, the
// lattice coordinate as integer I + local fraction F (the frame's origin split on the CPU)
const NOISE = `
uniform sampler2D uGrad;      // 256 x 1 RG32F: GRAD_X, GRAD_Z (float32, as the JS tables)
uniform vec4 uFM[8];          // fixed frames: (fx, fz) = (M.x dx + M.y dz, M.z dx + M.w dz) + origin
uniform vec4 uFO[8];          // (I.x, I.z, F.x, F.z) at the slot origin
uniform uint uFS[8];          // (seed 40503) >>> 0
uniform vec4 uKeep;           // octaveKeep(47, 21, 9.5, 4.3 m) at the texel
float gGrad(uint hx, uint hz, float dx, float dz) {
  uint h = hx + hz;
  h = (h ^ (h >> 13u)) * 1274126177u;
  h = h ^ (h >> 16u);
  vec2 g = texelFetch(uGrad, ivec2(int(h >> 24u), 0), 0).rg;
  return g.x * dx + g.y * dz;
}
float gnoiseAt(vec2 I, vec2 F, uint sh) {
  vec2 fl = floor(F), t = F - fl;
  ivec2 ii = ivec2(I + fl);
  uint hx0 = uint(ii.x) * 374761393u, hz0 = uint(ii.y) * 668265263u + sh;
  uint hx1 = hx0 + 374761393u, hz1 = hz0 + 668265263u;
  float u = t.x * t.x * t.x * (t.x * (t.x * 6.0 - 15.0) + 10.0);
  float v = t.y * t.y * t.y * (t.y * (t.y * 6.0 - 15.0) + 10.0);
  float n0 = gGrad(hx0, hz0, t.x, t.y) * (1.0 - u) + gGrad(hx1, hz0, t.x - 1.0, t.y) * u;
  float n1 = gGrad(hx0, hz1, t.x, t.y - 1.0) * (1.0 - u) + gGrad(hx1, hz1, t.x - 1.0, t.y - 1.0) * u;
  return clamp(0.5 + (n0 * (1.0 - v) + n1 * v), 0.0, 1.0);
}
float gnoiseM(vec4 M, vec4 O, uint sh, vec2 d) {
  return gnoiseAt(O.xy, O.zw + vec2(M.x * d.x + M.y * d.y, M.z * d.x + M.w * d.y), sh);
}
float gnoiseFx(int f, vec2 d) { return gnoiseM(uFM[f], uFO[f], uFS[f], d); }
// _fbm of a wavelength pair (frames fa, fa + 1): amp 1, 0.55; faded octaves at their mean
float fbmPair(int fa, float ka, float kb, vec2 d) {
  float o = ka > 0.0 ? 0.5 + ka * (gnoiseFx(fa, d) - 0.5) : 0.5;
  o += kb > 0.0 ? 0.55 * (0.5 + kb * (gnoiseFx(fa + 1, d) - 0.5)) : 0.55 * 0.5;
  return o / 1.55;
}
// _patch_field: the 47 / 21 m field, domain-warped (120 m, 25 m)
float patchField(vec2 d) {
  float wx = 25.0 * (2.0 * gnoiseFx(0, d) - 1.0), wz = 25.0 * (2.0 * gnoiseFx(1, d) - 1.0);
  return fbmPair(2, uKeep.x, uKeep.y, d + vec2(wx, wz));
}
// groundref patchAt: direct at texel 1.5 >= 1 m, else the world 1 m lattice, bilinear
float patchAt(vec2 d) {
  if (uTexel * 1.5 >= 1.0) return patchField(d);
  vec2 c0 = floor(d), t = d - c0, u = 1.0 - t;
  float p = patchField(c0) * u.x + patchField(c0 + vec2(1.0, 0.0)) * t.x;
  float q = patchField(c0 + vec2(0.0, 1.0)) * u.x + patchField(c0 + vec2(1.0, 1.0)) * t.x;
  return p * u.y + q * t.y;
}
float midAt(vec2 d) { return fbmPair(4, uKeep.z, uKeep.w, d); }
`;

// the class window: compact class (id order) | 0x80 sheet bed, of the 1 m map (S = 1) or the
// coarse grid (S = its stride); reads clamp to the map first (groundref's clamps)
const CLASSWIN = `
uniform usampler2D uCls;
uniform ivec4 uClsW;          // (window x0, z0 in S cells, S, 0)
uniform ivec2 uWorld;         // the class map's W, H (1 m cells)
uint clsAt(int cx, int cz) {
  ivec2 c = clamp(ivec2(cx, cz), ivec2(0), uWorld - 1) / uClsW.z - uClsW.xy;
  return texelFetch(uCls, c, 0).r;
}
// the nearest class of a raster texel (paint's cm_win: trunc, clipped): local -> world cell
uint clsTexel(ivec2 q) { vec2 d = dOf(q); return clsAt(uX0.x + int(floor(d.x)), uX0.y + int(floor(d.y))); }
`;

// (exported with albedoGLSL, fixedFrames, genomeRecRows and GLSL_CHUNKS: the ring painter builds on them)
export function genomeGLSL() {
  const mi = M_INV;
  return `
uniform sampler2D uRec;       // RGBA32F, RW x records: the genome record table at the texel
uniform sampler2D uGz;        // 513 x 1 R32F: the Gaussianising table
const float LD = ${fl(6 / 29)};
const vec3 WHITE = ${v3(WHITE)};
const mat3 MINV_T = mat3(${v3(mi[0])}, ${v3(mi[1])}, ${v3(mi[2])});
vec4 REC(int r, int k) { return texelFetch(uRec, ivec2(k, r), 0); }
float RZ(int r, int i) { return REC(r, 1 + i / 4)[i % 4]; }
float RY(int r, int i) { return REC(r, 6 + i / 4)[i % 4]; }
// genometex remap: piecewise linear through (zk, yk), linear past the ends
float remapR(int r, float z) {
  const int n = 17;
  float z0 = RZ(r, 0), zn = RZ(r, n - 1);
  if (z < z0) return RY(r, 0) + (z - z0) * ((RY(r, 1) - RY(r, 0)) / max(RZ(r, 1) - z0, 1e-12));
  if (z > zn) return RY(r, n - 1) + (z - zn) * ((RY(r, n - 1) - RY(r, n - 2)) / max(zn - RZ(r, n - 2), 1e-12));
  if (z == zn) return RY(r, n - 1);
  // the last knot j <= n - 2 with zj <= z (the knots ascend): a binary search, 4 reads, not up to 15
  int lo = 0, hi = n - 2;
  while (lo < hi) { int mid = (lo + hi + 1) / 2; if (RZ(r, mid) <= z) lo = mid; else hi = mid - 1; }
  int j = lo;
  return ((RY(r, j + 1) - RY(r, j)) / (RZ(r, j + 1) - RZ(r, j))) * (z - RZ(r, j)) + RY(r, j);
}
float gaussT(float n) {
  float u = n * 512.0, i0 = clamp(floor(u), 0.0, 511.0), t = u - i0;
  int i = int(i0);
  return texelFetch(uGz, ivec2(i, 0), 0).r * (1.0 - t) + texelFetch(uGz, ivec2(i + 1, 0), 0).r * t;
}
// genometex genomeTexel: the linear multiplier at the texel (mean 1 per channel)
vec3 genomeT(int r, vec2 d) {
  float zs = 0.0, zp = 0.0;
  // the octaves' (alpha, beta, seed) rows read once, not once an octave
  vec4 A[2] = vec4[2](REC(r, 11), REC(r, 12)), B[2] = vec4[2](REC(r, 13), REC(r, 14)), H[2] = vec4[2](REC(r, 45), REC(r, 46));
  for (int o = 0; o < 8; o++) {
    float al = A[o / 4][o % 4], be = B[o / 4][o % 4];
    if (al == 0.0 && be == 0.0) continue;
    uint sh = uint(H[o / 4][o % 4]) * 40503u;
    float n = gaussT(gnoiseM(REC(r, 29 + o), REC(r, 37 + o), sh, d));
    zs += al * n;
    zp += be * n;
  }
  float ey = exp(remapR(r, zs));
  int np = int(REC(r, 0).x), e = 0;
  while (e < np - 1 && REC(r, 15 + e / 4)[e % 4] <= zp) e++;
  return REC(r, 17 + e).rgb * ey;
}
vec3 labLinG(vec3 L) {
  float fy = (L.x + 16.0) / 116.0;
  vec3 f = vec3(fy + L.y / 500.0, fy, fy - L.z / 200.0), xyz;
  for (int k = 0; k < 3; k++) xyz[k] = (f[k] > LD ? f[k] * f[k] * f[k] : 3.0 * LD * LD * (f[k] - 4.0 / 29.0)) * WHITE[k];
  return xyz * MINV_T;
}
// genome.class_mean: mean_lin's Lab + the elev / slope terms
vec3 classMeanR(int r, float h, float sdeg) {
  vec4 a = REC(r, 26), b = REC(r, 27), c = REC(r, 28);
  float de = (h - a.w) / 100.0, ds = (sdeg - b.w) / 10.0;
  return max(labLinG(a.xyz + de * b.xyz + ds * c.xyz), vec3(1e-6));
}
`;
}

// the exemplar tiling (gs-family-wet-gravel; groundref tileTexel): up to TILE_MAX tiled records a slot
export const TILE_MAX = 4;
export function tilingGLSL() {
  return `
uniform sampler2D uExG;       // RGBA32F n x rows: the tiled records' Gaussianised box mips (groundref tilePrep G)
uniform sampler2D uExL;       // RGBA32F LUT_N x rows: their level LUTs (rgb)
uniform ivec4 uExR;           // the genome record row of tiled slot t (-1 = none: the genome T)
uniform ivec4 uExI[${TILE_MAX}];      // per tiled slot: its level's edge n_l, first G row, LUT row
uniform uvec4 uExS;           // their seeds (groundref tileSeed)
uniform vec4 uExP[${TILE_MAX}];       // their level texel (m), window margin (m), centre span (m), lattice edge S (m)
uniform ivec4 uExO[${TILE_MAX}];      // the slot origin's lattice coords: integer part (xy)
uniform vec4 uExF[${TILE_MAX}];       // ... their fraction (xy)
uint tileHash(ivec2 v, uint s) {
  uint h = (uint(v.x) * 0x27d4eb2du) ^ (uint(v.y) * 0x165667b1u) ^ (s * 0x9e3779b9u);
  h = (h ^ (h >> 15)) * 0x85ebca6bu;
  h = (h ^ (h >> 13)) * 0xc2b2ae35u;
  return h ^ (h >> 16);
}
int tileOf(int r) {
  for (int t = 0; t < ${TILE_MAX}; t++) if (uExR[t] == r) return t;
  return -1;
}
vec3 exG(ivec4 I, ivec2 a) { return texelFetch(uExG, ivec2(a.x, I.y + a.y), 0).rgb; }
// groundref tileTexel at slot-local d: the triangle's 3 vertices, each a hashed window of the level
vec3 tileT(int t, vec2 d) {
  const float R3 = ${fl(Math.sqrt(3))};
  vec4 P = uExP[t];
  ivec4 I = uExI[t];
  vec2 F = uExF[t].xy, sk = F + vec2(d.x - d.y / R3, 2.0 * d.y / R3) / P.w, b = floor(sk), f = sk - b;
  float fz = 1.0 - f.x - f.y;
  ivec2 v0, v1, v2;
  vec3 w;
  if (fz > 0.0) { v0 = ivec2(b); v1 = ivec2(b) + ivec2(0, 1); v2 = ivec2(b) + ivec2(1, 0); w = vec3(fz, f.y, f.x); }
  else { v0 = ivec2(b) + ivec2(1, 1); v1 = ivec2(b) + ivec2(1, 0); v2 = ivec2(b) + ivec2(0, 1); w = vec3(-fz, 1.0 - f.y, 1.0 - f.x); }
  vec3 acc = vec3(0.0);
  float w2 = 0.0;
  for (int c = 0; c < 3; c++) {
    if (!(w[c] > 0.0)) continue;
    ivec2 v = c == 0 ? v0 : c == 1 ? v1 : v2, V = uExO[t].xy + v;
    uint h1 = tileHash(V, uExS[t]), h2 = tileHash(V, uExS[t] + 1u);
    vec2 vl = vec2(v) - F, pv = P.w * vec2(vl.x + vl.y * 0.5, vl.y * (R3 * 0.5));
    vec2 q = P.y + vec2(float(h1 >> 8u), float(h2 >> 8u)) * (P.z / 16777216.0) + (d - pv);
    vec2 u = q / P.x - 0.5, fl = floor(u), tt = u - fl;
    ivec2 a = clamp(ivec2(fl), ivec2(0), ivec2(I.x - 1)), e = clamp(ivec2(fl) + 1, ivec2(0), ivec2(I.x - 1));
    vec3 g0 = exG(I, a) * (1.0 - tt.x) + exG(I, ivec2(e.x, a.y)) * tt.x;
    vec3 g1 = exG(I, ivec2(a.x, e.y)) * (1.0 - tt.x) + exG(I, e) * tt.x;
    acc += w[c] * (g0 * (1.0 - tt.y) + g1 * tt.y);
    w2 += w[c] * w[c];
  }
  vec3 x = acc / sqrt(w2), T;
  for (int c = 0; c < 3; c++) {
    float u = clamp((x[c] + ${fl(TILE.LUT_X)}) * ${fl((TILE.LUT_N - 1) / (2 * TILE.LUT_X))}, 0.0, ${fl(TILE.LUT_N - 1)});
    float i0 = min(floor(u), ${fl(TILE.LUT_N - 2)}), tt = u - i0;
    T[c] = texelFetch(uExL, ivec2(int(i0), I.z), 0)[c] * (1.0 - tt) + texelFetch(uExL, ivec2(int(i0) + 1, I.z), 0)[c] * tt;
  }
  return T;
}
`;
}

// the albedo pass's per-class GLSL; o.tiling: the genome T of a tiled record is tilingGLSL's tileT
// (FS_ALBEDO's; the default is the plain genome T)
export function albedoGLSL(o = {}) {
  const P = PAL.palette;
  return `
uniform vec4 uCB[${NCMAX}];   // the hand palette: base rgb, mid
uniform vec4 uCP[${NCMAX}];   // patch rgb
uniform ivec4 uCI[${NCMAX}];  // mode (0 genome, 1 palette, 2 heath / wetland, 3 rock, 4 sport, 5 water), record row, measured, sealed (SEALED-MAT)
uniform int uNC;
uniform int uGravelC;
uniform usampler2D uRes;      // residual codes (C5), the whole grid, RGBA8UI
uniform ivec3 uResN;          // w, h, on
uniform vec3 uResO;           // (slot x0 - res x0, slot z0 - res z0, cell)
uniform usampler2D uS2;       // s2col, RGBA8UI (0 0 0 = none)
uniform ivec3 uS2N;
uniform vec3 uS2O;
uniform vec2 uAnchor;         // anchor, grass_sat
uniform float uSportPh;       // x0 mod 8 (the sport stripes' phase)
const vec3 WATER_DEEP = ${v3(P.water.deep)};
float srgbDecF(float c) { return c <= 0.04045 ? c / 12.92 : pow((c + 0.055) / 1.055, 2.4); }
float srgbEncF(float c) { c = clamp(c, 0.0, 1.0); return c <= 0.0031308 ? c * 12.92 : 1.055 * pow(c, 1.0 / 2.4) - 0.055; }
vec4 bsW(float u, out float f) {
  f = floor(u);
  float t = u - f, t2 = t * t, t3 = t2 * t;
  return vec4((1.0 - t) * (1.0 - t) * (1.0 - t) / 6.0, (3.0 * t3 - 6.0 * t2 + 4.0) / 6.0, (-3.0 * t3 + 3.0 * t2 + 3.0 * t + 1.0) / 6.0, t3 / 6.0);
}
// surfacegen _bspline of the residual codes at local d (clamped taps)
vec3 bsRes(vec2 d) {
  vec2 p = uResO.xy + d;
  float fx, fz;
  vec4 wx = bsW(p.x / uResO.z - 0.5, fx), wz = bsW(p.y / uResO.z - 0.5, fz);
  vec3 acc = vec3(0.0);
  for (int b = 0; b < 4; b++) {
    int r = clamp(int(fz) + b - 1, 0, uResN.y - 1);
    vec3 t = vec3(0.0);
    for (int a = 0; a < 4; a++) t += wx[a] * vec3(texelFetch(uRes, ivec2(clamp(int(fx) + a - 1, 0, uResN.x - 1), r), 0).rgb);
    acc += wz[b] * t;
  }
  return acc;
}
// s2col: (the linear colour's bspline, the coverage's bspline)
vec4 bsS2(vec2 d) {
  vec2 p = uS2O.xy + d;
  float fx, fz;
  vec4 wx = bsW(p.x / uS2O.z - 0.5, fx), wz = bsW(p.y / uS2O.z - 0.5, fz);
  vec4 acc = vec4(0.0);
  for (int b = 0; b < 4; b++) {
    int r = clamp(int(fz) + b - 1, 0, uS2N.y - 1);
    vec4 t = vec4(0.0);
    for (int a = 0; a < 4; a++) {
      uvec3 c = texelFetch(uS2, ivec2(clamp(int(fx) + a - 1, 0, uS2N.x - 1), r), 0).rgb;
      float on = (c.r | c.g | c.b) != 0u ? 1.0 : 0.0;
      vec3 lin = vec3(srgbDecF(float(c.r) / 255.0), srgbDecF(float(c.g) / 255.0), srgbDecF(float(c.b) / 255.0)) * on;
      t += wx[a] * vec4(lin, on);
    }
    acc += wz[b] * t;
  }
  return acc;
}
// surfacegen measured_lin
vec3 measuredLin(vec3 c) {
  c = max(c, vec3(1e-5)) * uAnchor.x;
  float y = 0.2126 * c.r + 0.7152 * c.g + 0.0722 * c.b;
  vec3 s = y * pow(c / y, vec3(uAnchor.y));
  float k = y / max(0.2126 * s.r + 0.7152 * s.g + 0.0722 * s.b, 1e-9);
  return s * k;
}
// THE TEXEL'S OWN TERMS (ground-paint-cost): bsRes / bsS2 / patchAt / midAt depend on the texel (d) alone,
// so each is made once a texel and only when a class reads it (pure: the same value at every use) — the
// patch + mid (18 gnoise at L0 / L1) only for a palette class, the s2 b-spline (16 taps, 48 pow) only for a
// measured one, and neither again for a second class in the box
bool tRs = false, tSs = false, tPm = false;
vec3 tRes;
vec4 tS2;
float tPt, tMid;
vec3 resAt(vec2 d) { if (!tRs) { tRes = bsRes(d); tRs = true; } return tRes; }
vec4 s2At(vec2 d) { if (!tSs) { tS2 = bsS2(d); tSs = true; } return tS2; }
void pmAt(vec2 d) { if (!tPm) { tPt = patchAt(d); tMid = midAt(d); tPm = true; } }
// genome mode (_genome_albedo): sRGB 0..255
vec3 genomeAlb(int r, bool meas, vec2 d, float elev, float slope) {
  ${o.tiling ? 'int t = tileOf(r);\n  vec3 T = t >= 0 ? tileT(t, d) : genomeT(r, d);' : 'vec3 T = genomeT(r, d);'}
  float sdeg = atan(slope) * (180.0 / PI);
  vec3 base = classMeanR(r, elev, sdeg);
  if (uResN.z != 0) base *= exp2((resAt(d) - 128.0) / ${fl(RES_PER_STOP)});
  base *= uAnchor.x;
  if (uS2N.z != 0 && meas) {
    vec4 s = s2At(d);
    if (s.w > 1e-6) base = s.w * measuredLin(s.rgb / s.w) + (1.0 - s.w) * base;
  }
  return vec3(srgbEncF(T.r * base.r), srgbEncF(T.g * base.g), srgbEncF(T.b * base.b)) * 255.0;
}
// the hand palette (_class_albedo, genome mode)
vec3 paletteAlb(int c, vec2 d, float elev, float slope) {
  pmAt(d);
  float pt = tPt, mid = tMid;
  vec4 B = uCB[c];
  vec3 P = uCP[c].rgb;
  vec3 col = B.rgb * (1.0 - pt) + P * pt + (mid - 0.5) * (2.0 * B.w);
  int mode = uCI[c].x;
  if (mode == 2) {
    float bl = 0.35 * smooth01(mid, 0.55, 0.75);
    col = col * (1.0 - bl) + P * bl;
  } else if (mode == 3) {
    float band = 0.5 + 0.5 * sin(elev * (2.0 * PI / 7.0) + pt * 5.0);
    float n = 7.0 / (max(slope, 1e-3) * uTexel);
    float amp = smooth01(slope, 0.7, 1.1) * smooth01(n, 3.0, 6.0);
    col *= 0.98 + amp * (0.16 * band - 0.08);
  } else if (mode == 4) {
    float st = 0.5 + 0.5 * sin((uSportPh + d.x) * (2.0 * PI / 8.0));
    col *= 0.97 + 0.06 * (0.5 + octKeep(8.0) * (st - 0.5));
  }
  return col;
}
vec3 classAlb(int c, vec2 d, uint here, float elev, float slope) {
  if (uCI[c].x == 5) {
    // WATER-G: one colour to the shore; a sheet lake's bed = the gravel's paint
    if ((here & 0x80u) == 0u) return WATER_DEEP;
    c = uGravelC;
  }
  if (uCI[c].x == 0) return genomeAlb(uCI[c].y, uCI[c].z != 0, d, elev, slope);
  return paletteAlb(c, d, elev, slope);
}
`;
}

// ---- the programs ------------------------------------------------------------------------
// TIN (stage 0 + the crown grid): one indexed draw per level-j node (NOSTAND / degenerate
// triangles dropped on the CPU), (x, z) local to the slot origin, y - yref; depth = the node's
// sequence in groundref's (ty, tx) order (LESS: the first node wins on a shared border; inside a
// node no tie: the map is shifted so the pixel centre samples at the texel centre + (TIN_EX,
// TIN_EZ) texels, off every RTIN edge and diagonal — groundref tinRaster — and the plane is
// evaluated back at the centre). The FLAT gradient = the screen derivatives of the planar height
// (exact on a triangle: helper texels evaluate its plane)
const VS_TIN = HEAD + `
in vec3 aP;                   // node-local (x, z, y - yn): the node's buffers serve every slot
uniform vec3 uOff;            // the node's origin from the slot's (integer m), yn - yref (integer)
uniform vec4 uMap;            // clip = (p - uMap.xy) uMap.zw - 1
uniform float uDepth;
out float vY;
void main() {
  vec3 p = aP + uOff;
  vY = p.z;
  gl_Position = vec4((p.xy - uMap.xy) * uMap.zw - 1.0, uDepth, 1.0);
}
`;
const FS_TIN = HEAD + `
uniform float uYref;
uniform float uPx;            // the target's texel (m)
in float vY;
out vec4 o;
uniform vec2 uE;              // (TIN_EX, TIN_EZ): the sample's offset from the texel centre (texels)
void main() {
  float hx = dFdx(vY), hz = dFdy(vY);
  o = vec4(vY - uE.x * hx - uE.y * hz + uYref, 1.0, hx / uPx, hz / uPx);
}
`;
// past every node: the nearest covered sample of the row (left, else right) ...
const FS_ROWFILL = HEAD + `
uniform sampler2D uT;
uniform ivec2 uN;
out vec4 o;
void main() {
  ivec2 q = ivec2(gl_FragCoord.xy);
  vec4 v = texelFetch(uT, q, 0);
  if (v.y == 1.0) { o = v; return; }
  for (int i = q.x - 1; i >= 0; i--) { vec4 w = texelFetch(uT, ivec2(i, q.y), 0); if (w.y == 1.0) { o = vec4(w.x, 2.0, w.zw); return; } }
  for (int i = q.x + 1; i < uN.x; i++) { vec4 w = texelFetch(uT, ivec2(i, q.y), 0); if (w.y == 1.0) { o = vec4(w.x, 2.0, w.zw); return; } }
  o = vec4(0.0);
}
`;
// ... then the column: a row with none copies the first covered row when no covered row lies
// before it, else the LAST covered row (groundref tinRaster's s = k < ka ? ka : kb)
const FS_COLFILL = HEAD + `
uniform sampler2D uT;
uniform ivec2 uN;
out vec4 o;
void main() {
  ivec2 q = ivec2(gl_FragCoord.xy);
  vec4 v = texelFetch(uT, q, 0);
  if (v.y > 0.0) { o = v; return; }
  bool before = false;
  for (int k = q.y - 1; k >= 0; k--) if (texelFetch(uT, ivec2(q.x, k), 0).y > 0.0) { before = true; break; }
  int s = -1;
  if (before) { for (int k = uN.y - 1; k >= 0; k--) if (texelFetch(uT, ivec2(q.x, k), 0).y > 0.0) { s = k; break; } }
  else { for (int k = q.y + 1; k < uN.y; k++) if (texelFetch(uT, ivec2(q.x, k), 0).y > 0.0) { s = k; break; } }
  vec4 w = s >= 0 ? texelFetch(uT, ivec2(q.x, s), 0) : vec4(0.0);
  o = vec4(w.x, s >= 0 ? 2.0 : 0.0, w.zw);
}
`;

// 1. albedo: paint()'s class blend
const FS_ALBEDO = () => HEAD + RASTER + NOISE + CLASSWIN + genomeGLSL() + tilingGLSL() + albedoGLSL({ tiling: true }) + `
uniform sampler2D uH;
uniform int uR;
uniform int uSealedWin;       // SEALED-WIN: this level draws no ways (j > WAYS_MAX_J)
out vec4 o;
void main() {
  ivec2 q = ivec2(gl_FragCoord.xy);
  vec2 d = dOf(q);
  vec4 H = texelFetch(uH, q, 0);
  float elev = H.x, slope = sqrt(H.w * H.w + H.z * H.z);
  uint here = clsTexel(q);
  int hc = int(here & 0x7fu);
  if (uSealedWin != 0 && uCI[hc].w != 0) { o = vec4(classAlb(hc, d, here, elev, slope), 0.0); return; }
  int i0 = max(0, q.x - uR), i1 = min(uPW.x - 1, q.x + uR), k0 = max(0, q.y - uR), k1 = min(uPW.y - 1, q.y + uR);
  float cnt[${NCMAX}];
  for (int c = 0; c < ${NCMAX}; c++) cnt[c] = 0.0;
  if (uTexel < 1.0) {
    // the 1 m cells under the box: texel counts per cell (exact)
    float ax = dOf(ivec2(i0, 0)).x - uTexel / 2.0, bx = dOf(ivec2(i1, 0)).x + uTexel / 2.0;
    float az = dOf(ivec2(0, k0)).y - uTexel / 2.0, bz = dOf(ivec2(0, k1)).y + uTexel / 2.0;
    for (int cz = int(floor(az)); float(cz) < bz; cz++) {
      float nz = rnd((min(bz, float(cz + 1)) - max(az, float(cz))) / uTexel);
      if (nz <= 0.0) continue;
      for (int cx = int(floor(ax)); float(cx) < bx; cx++) {
        float n = rnd((min(bx, float(cx + 1)) - max(ax, float(cx))) / uTexel);
        if (n > 0.0) cnt[int(clsAt(uX0.x + cx, uX0.y + cz) & 0x7fu)] += n * nz;
      }
    }
  } else {
    for (int kk = k0; kk <= k1; kk++) for (int ii = i0; ii <= i1; ii++) cnt[int(clsTexel(ivec2(ii, kk)) & 0x7fu)] += 1.0;
  }
  float tot = float((i1 - i0 + 1) * (k1 - k0 + 1)), ws = 0.0;
  vec3 acc = vec3(0.0);
  for (int c = 0; c < uNC; c++) {
    if (!(cnt[c] > 0.0)) continue;
    float w = cnt[c] / tot;
    acc += w * classAlb(c, d, here, elev, slope);
    ws += w;
  }
  o = vec4(acc / max(ws, 1e-6), 0.0);
}
`;

// 2. the ways: groundways.js (prepWays' world constants in as GLSL constants)
function waysGLSL(ST) {
  const K = WAYS_K, L = WAYSLOOK, C = L.colour, hc = C.host, lm = ST.luma, g = MARKS_IN.ground || null;
  const pidx = new Array(16).fill(-1);
  K.PATH_KINDS.forEach((k, i) => { pidx[L.kinds[k]] = i; });
  const WM = [];
  for (let k = 0; k < 16; k++) WM.push(v4([ST.M[3 * k], ST.M[3 * k + 1], ST.M[3 * k + 2], ST.Many[k] ? 1 : 0]));
  const XM = [];
  for (const k of K.PATH_KINDS) for (let hi = 0; hi < 16; hi++) {
    const m = ST.crossMean.get(L.kinds[k] | (hi << K.KIND_BITS));
    XM.push(v4([m.tread || 0, m.rut || 0, m.shoulder || 0, m.host || 0]));
  }
  const PT = [], PP = [], PS = [], PR = [];
  for (const k of K.PATH_KINDS) {
    const c = C[k];
    PT.push(v4([...c.tread, c.host_keep])); PP.push(v4([...c.patch, c.host_aware || 0]));
    PS.push(v4([...c.shoulder, c.worn || 0])); PR.push(v4([...(c.rut || [0, 0, 0]), 0]));
  }
  const ec = C.contrast, wn = C.worn, tmin = C.cross.tread_min_m, rad = (v) => (v * Math.PI) / 180;
  const XYZR = [[0.4124564, 0.3575761, 0.1804375], [0.2126, 0.7152, 0.0722], [0.0193339, 0.1191920, 0.9503041]];
  const sac = L.share_by_sac, SAC = Array.from({ length: 16 }, (_, i) => fl(sac[Math.min(i, sac.length - 1)]));
  const tr = L.track, hg = tr.centre_host_by_grade;
  const s = L.steps, bl = L.ballast;
  const gz = (o, k, d = 0) => (g && g[o] && g[o][k] !== undefined ? g[o][k] : d);
  const gc = (o) => v3(g && g[o] ? g[o].colour : [0, 0, 0]);
  return `
const float R_OFF = ${fl(K.R_OFF)}, R_SCALE = ${fl(K.R_SCALE)}, G_OFF = ${fl(K.G_OFF)}, G_SCALE = ${fl(K.G_SCALE)};
const float A_OFF = ${fl(K.A_OFF)}, A_SCALE = ${fl(K.A_SCALE)}, L_OFF = ${fl(K.L_OFF)}, L_SCALE = ${fl(K.L_SCALE)};
const float D_OFF = ${fl(K.D_OFF)}, D_SCALE = ${fl(K.D_SCALE)}, P_OFF = ${fl(K.P_OFF)};
const int KIND_BITS = ${K.KIND_BITS}, KMASK = ${K.KMASK}, CODE_BITS = ${K.CODE_BITS}, CMASK = ${K.CMASK}, MARK0 = ${K.MARK0};
const int C_CENTRE = ${K.CODE.centre}, C_LANE = ${K.CODE.lane}, C_SLEEPERS = ${K.CODE.sleepers}, C_TREADS = ${K.CODE.treads};
const int C_ZEBRA = ${K.CODE.zebra}, C_XLINE = ${K.CODE.xline}, C_GRID = ${K.CODE.grid};
const int TEETH_HB0 = ${K.TEETH_HB0};
const int K_TRACK = ${L.kinds.track}, K_STEPS = ${ST.kSteps};
const float KIND_BLEND_TEXEL = ${fl(K.KIND_BLEND_TEXEL)};
const int PIDX[16] = ${arr('int', pidx.map(String))};
const vec4 WM[16] = ${arr('vec4', WM)};
const vec4 XM[${XM.length}] = ${arr('vec4', XM)};
const vec4 PT[3] = ${arr('vec4', PT)};
const vec4 PP[3] = ${arr('vec4', PP)};
const vec4 PS[3] = ${arr('vec4', PS)};
const vec4 PR[3] = ${arr('vec4', PR)};
const float SAC[16] = ${arr('float', SAC)};
const float TR_HG[${hg.length}] = ${arr('float', hg.map(fl))};
const float TR_R0 = ${fl(tr.rut_t[0])}, TR_R1 = ${fl(tr.rut_t[1])}, TR_CT = ${fl(tr.centre_t)}, TR_RS = ${fl(C.track.rut_soft_t)};
const float CR_TT = ${fl(C.cross.tread_t)}, CR_ST = ${fl(C.cross.soft_t)};
const float CR_TMIN[${tmin.length}] = ${arr('float', tmin.map(fl))};
const int CT_EON = ${ec.de_min > 0 || ec.c_min > 0 ? 1 : 0};
const int CT_EK[3] = ${arr('int', K.PATH_KINDS.map((k) => (ec.earth_kinds.includes(k) ? '1' : '0')))};
const float CT_DE = ${fl(ec.de_min)}, CT_CMIN = ${fl(ec.c_min)}, CT_CMAX = ${fl(ec.c_max)}, CT_H0 = ${fl(rad(ec.hue[0]))}, CT_H1 = ${fl(rad(ec.hue[1]))};
const float CT_VEGA = ${fl(ec.veg_a)}, WN_GAIN = ${fl(wn.gain)};
const vec3 WN_WARM = ${v3(wn.warm)};
const vec3 EW_WHITE = vec3(0.95047, 1.0, 1.08883);
const mat3 EW_XYZ = mat3(${v3(XYZR[0])}, ${v3(XYZR[1])}, ${v3(XYZR[2])});      // v * EW_XYZ = XYZ v (rows)
const mat3 EW_XYZI = mat3(${v3(XYZ_INV[0])}, ${v3(XYZ_INV[1])}, ${v3(XYZ_INV[2])});
const float H_RING = ${fl(hc.ring_m)}, H_SAT = ${fl(hc.sat)};
const vec3 H_WARM = ${v3(hc.warm)};
const vec4 H_GAIN = ${v4([hc.gain.tread, hc.gain.patch, hc.gain.shoulder, hc.gain.rut])};
const float CT_DL = ${fl(C.contrast.dl_min)}, SIDE_M = ${fl(K.SIDE_M)};
const float NZ_MID = ${fl(L.noise.mid)};
const int LM_ON = ${lm ? 1 : 0};
const float LM_RING = ${fl(lm ? lm.ring_m : 0)}, LM_K0 = ${fl(lm ? lm.ring_k[0] : 0)}, LM_K1 = ${fl(lm ? lm.ring_k[1] : 0)};
const float LM_FLOOR = ${fl(lm ? lm.floor : 0)}, LM_CEIL = ${fl(lm ? lm.ceil : 0)}, LM_NK = ${fl(lm ? lm.nosing_k : 0)}, LM_RK = ${fl(lm ? lm.riser_k : 0)};
const float ST_L0 = ${fl(ST.L0)};
const float ST_PERIOD = ${fl(s.phase_period_m)}, ST_NOS = ${fl(s.nosing_m)}, ST_RISER = ${fl(s.riser_m)};
const vec3 ST_CN = ${v3(s.colour.nosing)}, ST_CR = ${v3(s.colour.riser)};
const vec3 KERB_C = ${v3(L.kerb.colour)}, LINES_C = ${v3(L.lines.colour)}, BL_C = ${v3(bl.colour_sleeper)};
const float KERB_M = ${fl(ST.kerbM)}, LINE_M = ${fl(ST.lineM)};
const float BL_PITCH = ${fl(bl.sleeper_pitch_m)}, BL_W = ${fl(bl.sleeper_w_m)}, BL_LEN = ${fl(bl.sleeper_len_m)};
const float ZB_BAR = ${fl(gz('zebra', 'bar_m'))}, ZB_GAP = ${fl(gz('zebra', 'gap_m'))}, ZB_MARGIN = ${fl(gz('zebra', 'margin_slots'))};
const vec3 ZB_C = ${gc('zebra')};
const float GW_DASH = ${fl(gz('give_way', 'dash_m'))}, GW_GAP = ${fl(gz('give_way', 'gap_m'))}, GW_LINE = ${fl(gz('give_way', 'line_m'))};
const vec3 GW_C = ${gc('give_way')};
const float SP_LINE = ${fl(gz('stop', 'line_m'))};
const vec3 SP_C = ${gc('stop')};
const float GR_MARGIN = ${fl(gz('grid', 'margin_slots'))}, GR_PITCH = ${fl(gz('grid', 'pitch_m', 1))}, GR_BAR = ${fl(gz('grid', 'bar_m'))};
const vec3 GR_PIT = ${v3(g && g.grid ? g.grid.pit : [0, 0, 0])}, GR_C = ${gc('grid')};
const float XO_SCALE = ${fl(g ? g.xline_o_scale : 1)};
const float TRI_BASE = ${fl(gz('give_way_tri', 'base_m'))}, TRI_LEN = ${fl(gz('give_way_tri', 'len_m', 1))}, TRI_GAP = ${fl(gz('give_way_tri', 'gap_m'))}, TRI_LGAP = ${fl(gz('give_way_tri', 'line_gap_m'))};
const vec3 TRI_C = ${gc('give_way_tri')};
uniform usampler2D uWF;       // the field window: texel (2 i + plane, j) = cell (wx0 + i, wz0 + j)
uniform ivec4 uWWin;          // wx0, wz0, the field's W1, H1
uniform sampler2D uWMk;       // markCells of the window, RGBA32F (F, C r g b)
uniform int uMarkOn;
uniform int uFine;
uniform vec4 uWT0;            // waysTile: kc, pk, amp, hh
uniform vec4 uWT1;            // ea, eb, wa, wb
uniform float uWN;            // the worn edge band's end (waysTile wn)
uniform vec4 uTKb[4];         // treadKa: the bytes ...
uniform vec4 uTKa[4];         // ... their ka
uniform int uTKn;
// WAYS-Q4 (groundways q4Texel): past the 1 m field the 4 m mass window, texel (3 (i - qx0) + plane, j - qz0) =
// cell (i, j)'s plane (0: entry ids, 1: their coverage /255, 2: x = the min R byte)
uniform usampler2D uQ4;
uniform ivec4 uQWin;          // qx0, qz0, the mass's QW, QH
uniform int uQOn;
const float Q4_M = ${fl(Q4.M)}, Q4_K = ${fl(Q4.K)};
int pathIdx(int k) { return PIDX[k]; }
uint byteAt(int j, int i, int c) {
  uvec4 v = texelFetch(uWF, ivec2(2 * (i - uWWin.x) + (c >> 2), j - uWWin.y), 0);
  return v[c & 3];
}
float fB(int j, int i, int c) { return float(byteAt(j, i, c)); }
struct Tap { int i0; int i1; int j0; int j1; float tx; float tz; };
Tap taps(float x, float z) {
  float u = x - 0.5, v = z - 0.5, fx = floor(u), fz = floor(v);
  Tap t;
  t.i0 = clamp(int(fx), 0, uWWin.z - 1); t.i1 = clamp(int(fx) + 1, 0, uWWin.z - 1); t.tx = clamp(u - fx, 0.0, 1.0);
  t.j0 = clamp(int(fz), 0, uWWin.w - 1); t.j1 = clamp(int(fz) + 1, 0, uWWin.w - 1); t.tz = clamp(v - fz, 0.0, 1.0);
  return t;
}
float bil4(float a, float b, float c, float d, Tap t) {
  float ux = 1.0 - t.tx, uz = 1.0 - t.tz;
  return (a * ux + b * t.tx) * uz + (c * ux + d * t.tx) * t.tz;
}
float bilB(Tap t, int c) { return bil4(fB(t.j0, t.i0, c), fB(t.j0, t.i1, c), fB(t.j1, t.i0, c), fB(t.j1, t.i1, c), t); }
float decR(float v) { return (v - R_OFF) / R_SCALE; }
float decG(float v) { return (v - G_OFF) / G_SCALE; }
ivec2 blockOf(float c, int n) {
  return ivec2(clamp(int(ceil(c - uTexel / 2.0 - 0.5)), 0, n), clamp(int(ceil(c + uTexel / 2.0 - 0.5)), 0, n));
}
bool sameTaps(Tap t, int c) {
  uint b = byteAt(t.j0, t.i0, c);
  return b == byteAt(t.j0, t.i1, c) && b == byteAt(t.j1, t.i0, c) && b == byteAt(t.j1, t.i1, c);
}
float npMod(float a, float b) { return a - b * floor(a / b); }
float boxCov(float x, float a, float b, float w) { return clamp((min(x + w / 2.0, b) - max(x - w / 2.0, a)) / w, 0.0, 1.0); }
float shareOf(int b) { int k = b & KMASK; return k == K_TRACK || pathIdx(k) < 0 ? 1.0 : SAC[b >> KIND_BITS]; }
// _path_cross: (tread, rut, shoulder, host) of look byte b at |t| = u; hw > 0 (the way's half
// width): the soil / rock tread's half-weight point widens to cross.tread_min_m[sac] / 2, never past the edge
vec4 crossOf(int b, float u, float hw) {
  int kind = b & KMASK, sac = b >> KIND_BITS;
  if (kind == K_TRACK) {
    float hcv = TR_HG[min(sac, ${hg.length - 1})] * (1.0 - smooth01(u, TR_CT - TR_RS, TR_CT + TR_RS));
    float sh = smooth01(u, TR_R1, TR_R1 + 2.0 * TR_RS);
    float rw = smooth01(u, TR_R0 - TR_RS, TR_R0 + TR_RS) * (1.0 - smooth01(u, TR_R1 - TR_RS, TR_R1 + TR_RS));
    float rest = 1.0 - hcv;
    return vec4(rest * (1.0 - sh) * (1.0 - rw), rest * (1.0 - sh) * rw, rest * sh, hcv);
  }
  float tt = CR_TT, tmin = CR_TMIN[min(sac, ${tmin.length - 1})];
  if (hw > 0.0 && tmin > 0.0) tt = max(tt + 0.5 * CR_ST, min(0.5 * tmin / hw, 1.0)) - 0.5 * CR_ST;
  float tw = 1.0 - smooth01(u, tt, tt + CR_ST);
  return vec4(tw, 0.0, 1.0 - tw, 0.0);
}
vec4 crossMeanOf(int b) { return XM[pathIdx(b & KMASK) * 16 + (b >> KIND_BITS)]; }
// groundways lstarY / pathFloor: CIE L* (D65, Rec. 709) of sRGB 0..255, the contrast floor (colour.contrast)
float srgbDecW(float x) { return x <= 0.04045 ? x / 12.92 : pow((x + 0.055) / 1.055, 2.4); }
float srgbEncW(float x) { return x <= 0.0031308 ? x * 12.92 : 1.055 * pow(x, 1.0 / 2.4) - 0.055; }
vec3 linOf(vec3 c) {
  vec3 x = clamp(c / 255.0, 0.0, 1.0);
  return vec3(srgbDecW(x.r), srgbDecW(x.g), srgbDecW(x.b));
}
float lstarOfY(float Y) { return Y > 216.0 / 24389.0 ? 116.0 * pow(Y, 1.0 / 3.0) - 16.0 : Y * (24389.0 / 27.0); }
float lstarOf(vec3 c) { return lstarOfY(dot(linOf(c), vec3(0.2126, 0.7152, 0.0722))); }
vec3 pathFloor(vec3 c, vec3 ref, float Lh, float sn) {
  vec3 lr = linOf(ref);
  float Y = dot(lr, vec3(0.2126, 0.7152, 0.0722)), Lc = lstarOfY(Y), dc = Lc - Lh;
  if (!(abs(dc) < CT_DL)) return c;
  float w = clamp((abs(dc) - 0.5 * CT_DL) / (0.5 * CT_DL), 0.0, 1.0);
  float sd = sn + ((dc >= 0.0 ? 1.0 : -1.0) - sn) * w;
  sd = Lh + CT_DL > 100.0 ? -1.0 : Lh - CT_DL < 0.0 ? 1.0 : sd;
  float Lt = Lh + CT_DL * sd;
  float s = (Lt > 8.0 ? pow((Lt + 16.0) / 116.0, 3.0) : Lt * (27.0 / 24389.0)) / max(Y, 1e-6);
  vec3 v = clamp(linOf(c) * s, 0.0, 1.0);
  return vec3(srgbEncW(v.r), srgbEncW(v.g), srgbEncW(v.b)) * 255.0;
}
// groundways labOfLin / linOfLab / earthFloor (colour.contrast's earth floor, paths-earth)
float labF(float t) { return t > 216.0 / 24389.0 ? pow(t, 1.0 / 3.0) : t * (24389.0 / 27.0 / 116.0) + 16.0 / 116.0; }
float labT(float f) { return f > 6.0 / 29.0 ? f * f * f : (f - 16.0 / 116.0) * (27.0 * 116.0 / 24389.0); }
vec3 labOfLinW(vec3 l) {
  vec3 t = (l * EW_XYZ) / EW_WHITE, f = vec3(labF(t.x), labF(t.y), labF(t.z));
  return vec3(116.0 * f.y - 16.0, 500.0 * (f.x - f.y), 200.0 * (f.y - f.z));
}
vec3 linOfLabW(vec3 L) {
  float fy = (L.x + 16.0) / 116.0;
  vec3 t = vec3(labT(fy + L.y / 500.0), labT(fy), labT(fy - L.z / 200.0)) * EW_WHITE;
  return clamp(t * EW_XYZI, 0.0, 1.0);
}
vec3 labOfW(vec3 c) { return labOfLinW(linOf(c)); }
// ref's hue clamped into [CT_H0, CT_H1], its chroma raised to CT_CMIN and along that hue to the larger
// root of each host's (hs: the ring Hs, hh: the texel's host H) CT_DE sphere (two passes), never past
// max(C, CT_CMAX); L* kept; the (a*, b*) step x v; c's linear rgb x target / ref
vec3 earthFloor(vec3 c, vec3 ref, vec3 hs, vec3 hh, float v) {
  vec3 lr = linOf(ref), lab = labOfLinW(lr);
  float C = length(lab.yz), hc = 0.5 * (CT_H0 + CT_H1), hw = 0.5 * (CT_H1 - CT_H0);
  float dh = npMod(atan(lab.z, lab.y) - hc + PI, 2.0 * PI) - PI, hn = hc + clamp(dh, -hw, hw);
  float ch = cos(hn), sh = sin(hn), C1 = max(C, CT_CMIN), de2 = CT_DE * CT_DE;
  for (int it = 0; it < 4; it++) {
    vec3 hl = (it & 1) == 0 ? hs : hh;
    float dL = lab.x - hl.x, e2 = dL * dL + (C1 * ch - hl.y) * (C1 * ch - hl.y) + (C1 * sh - hl.z) * (C1 * sh - hl.z);
    if (e2 < de2) {
      float pp = hl.y * ch + hl.z * sh, q = hl.y * hl.y + hl.z * hl.z + dL * dL - de2;
      C1 = max(C1, pp + sqrt(max(pp * pp - q, 0.0)));
    }
  }
  if (!(v > 0.0 && (abs(dh) > hw || C1 > C))) return c;
  float C2 = min(C1, max(C, CT_CMAX));
  vec3 tl = linOfLabW(vec3(lab.x, lab.y + v * (C2 * ch - lab.y), lab.z + v * (C2 * sh - lab.z)));
  vec3 x = clamp(linOf(c) * (tl / max(lr, vec3(1e-6))), 0.0, 1.0);
  return vec3(srgbEncW(x.r), srgbEncW(x.g), srgbEncW(x.b)) * 255.0;
}
// groundways hostPass's (hb, Hs rgb) at raster texel q: the column sums of the row sums (wboxh)
uniform sampler2D uS0;
uniform sampler2D uS1;
uniform sampler2D uS2;
uniform ivec2 uRad;
vec4 hostAt(ivec2 q) {
  int rh = uRad.x;
  int y0 = max(0, q.y - rh), y1 = min(uPW.y - 1, q.y + rh);
  vec4 a = vec4(0.0), b = vec4(0.0);
  float c8 = 0.0;
  for (int k = y0; k <= y1; k++) { ivec2 p = ivec2(q.x, k); a += texelFetch(uS0, p, 0); b += texelFetch(uS1, p, 0); c8 += texelFetch(uS2, p, 0).x; }
  float nh = float((y1 - y0 + 1) * (min(uPW.x - 1, q.x + rh) - max(0, q.x - rh) + 1));
  a /= nh; b /= nh; c8 /= nh;
  bool ok = a.x > 1e-3;
  return vec4(clamp(ok ? a.y / a.x : a.z, 0.0, 1.0), ok ? vec3(a.w, b.x, b.y) / a.x : vec3(b.z, b.w, c8));
}
// groundways sideNodes / sideMean: the side lattice's bilinear sign mean at raster texel q for path pi
// (x: the tread mean, y: the shoulder, z: the rut), each node's sign over its own host ring
vec3 sideMean(ivec2 q, int pi, float ha) {
  float sp = max(SIDE_M, 2.0 * uTexel), X = Xr(q.x), Z = Zr(q.y);
  float Xn = floor(X / sp) * sp, Zn = floor(Z / sp) * sp, fx = (X - Xn) / sp, fz = (Z - Zn) / sp;
  int st = int(roundEven(sp / uTexel));
  int i0 = int(roundEven((Xn - float(uX0.x)) / uTexel)) + uM, j0 = int(roundEven((Zn - float(uX0.y)) / uTexel)) + uM;
  ivec2 ni = clamp(ivec2(i0, i0 + st), 0, uPW.x - 1), nj = clamp(ivec2(j0, j0 + st), 0, uPW.y - 1);
  vec3 sg[4];
  for (int n = 0; n < 4; n++) {
    vec4 h = hostAt(ivec2((n & 1) == 0 ? ni.x : ni.y, n < 2 ? nj.x : nj.y));
    float Lhn = lstarOf(h.yzw), kn = h.x * ha, Ln = lum(h.yzw);
    vec3 hd = Ln + H_SAT * (h.yzw - Ln);
    vec3 r0 = (PT[pi].rgb + PP[pi].rgb) * 0.5, r1 = PS[pi].rgb, r2 = PR[pi].rgb;
    if (ha > 0.0) {
      r0 = r0 * (1.0 - kn) + (hd * (0.5 * (H_GAIN.x + H_GAIN.y)) + H_WARM) * kn;
      r1 = r1 * (1.0 - kn) + (hd * H_GAIN.z + H_WARM) * kn;
      r2 = r2 * (1.0 - kn) + (hd * H_GAIN.w + H_WARM) * kn;
    }
    sg[n] = vec3(lstarOf(r0) >= Lhn ? 1.0 : -1.0, lstarOf(r1) >= Lhn ? 1.0 : -1.0, lstarOf(r2) >= Lhn ? 1.0 : -1.0);
  }
  return (1.0 - fz) * ((1.0 - fx) * sg[0] + fx * sg[1]) + fz * ((1.0 - fx) * sg[2] + fx * sg[3]);
}
// addPath: o + w (path colour - host); H the host colour, hx = (hb, Hs rgb), nb the worn edge band's weight
vec3 addPath(vec3 o, vec3 H, vec2 d, ivec2 q, int bv, float w, vec4 cf, vec4 hx, float nb) {
  int pi = pathIdx(bv & KMASK);
  float p = 0.5 + uWT0.y * (gnoiseFx(6, d) - 0.5);
  float m = (midAt(d) - 0.5) * (2.0 * NZ_MID);
  float ha = PP[pi].w, k = hx.x * ha, L = lum(hx.yzw);
  vec3 tread = PT[pi].rgb * (1.0 - p) + PP[pi].rgb * p, shoulder = PS[pi].rgb, rut = PR[pi].rgb;
  vec3 tm = (PT[pi].rgb + PP[pi].rgb) * 0.5;
  if (ha > 0.0) {
    vec3 hd = L + H_SAT * (hx.yzw - L);
    tread = tread * (1.0 - k) + (hd * (H_GAIN.x * (1.0 - p) + H_GAIN.y * p) + H_WARM) * k;
    tm = tm * (1.0 - k) + (hd * (0.5 * (H_GAIN.x + H_GAIN.y)) + H_WARM) * k;
    shoulder = shoulder * (1.0 - k) + (hd * H_GAIN.z + H_WARM) * k;
    rut = rut * (1.0 - k) + (hd * H_GAIN.w + H_WARM) * k;
  }
  bool trk = (bv & KMASK) == K_TRACK;
  vec3 tmf = tm;
  if (CT_DL > 0.0) {
    float Lh = lstarOf(hx.yzw);
    // the side lattice only where a floor acts (its 4 host-ring taps)
    if (abs(lstarOf(tm) - Lh) < CT_DL || abs(lstarOf(shoulder) - Lh) < CT_DL || (trk && abs(lstarOf(rut) - Lh) < CT_DL)) {
      vec3 sn = sideMean(q, pi, ha);
      tread = pathFloor(tread, tm, Lh, sn.x);
      tmf = pathFloor(tm, tm, Lh, sn.x);
      shoulder = pathFloor(shoulder, shoulder, Lh, sn.y);
      if (trk) rut = pathFloor(rut, rut, Lh, sn.z);
    }
  }
  shoulder = shoulder * (1.0 - PT[pi].w) + H * PT[pi].w;
  // the earth floor (paths-earth): over a green host (the ring's CIE a* < 0) an earth hue, CT_CMIN, CT_DE
  bool earth = CT_EON != 0 && CT_EK[pi] != 0;
  float vg = 0.0;
  if (earth || nb > 0.0) {
    vec3 hsl = labOfW(hx.yzw);
    vg = clamp(-hsl.y / CT_VEGA, 0.0, 1.0);
    if (earth) {
      vec3 hl = labOfW(H);
      tread = earthFloor(tread, tmf, hsl, hl, vg);
      shoulder = earthFloor(shoulder, shoulder, hsl, hl, vg);
      if (trk) rut = earthFloor(rut, rut, hsl, hl, vg);
    }
  }
  vec3 pc = cf.x * (tread + m) + cf.y * (rut + m) + cf.z * shoulder + cf.w * H;
  o = o + w * (pc - H);
  // the worn edge band: trampled host grass, lighter and yellower, over a green host only
  if (nb > 0.0) o += nb * vg * (H * (WN_GAIN - 1.0) + WN_WARM);
  return o;
}
// _path_kinds, fine regime: (look byte, weight | -1 = null) under the [1 2 1] tent of the taps
float tentOf(ivec4 cj, ivec4 ci, int t, uint v) {
  const vec3 W = vec3(0.25, 0.5, 0.25);
  float s = 0.0;
  for (int dj = -1; dj <= 1; dj++) for (int di = -1; di <= 1; di++) {
    int j = clamp(cj[t] + dj, 0, uWWin.w - 1), i = clamp(ci[t] + di, 0, uWWin.z - 1);
    uint b = byteAt(j, i, 2);
    if (v == 0u ? (b == 0u && byteAt(j, i, 0) < 255u) : b == v) s += W[dj + 1] * W[di + 1];
  }
  return s;
}
int texelKinds(Tap tp, out uint bs[${MAXB}], out float bw[${MAXB}]) {
  ivec4 cj = ivec4(tp.j0, tp.j0, tp.j1, tp.j1), ci = ivec4(tp.i0, tp.i1, tp.i0, tp.i1);
  int n = 0;
  bool none = false;
  for (int t = 0; t < 4; t++) for (int dj = -1; dj <= 1; dj++) for (int di = -1; di <= 1; di++) {
    int j = clamp(cj[t] + dj, 0, uWWin.w - 1), i = clamp(ci[t] + di, 0, uWWin.z - 1);
    uint b = byteAt(j, i, 2);
    if (b != 0u) {
      bool seen = false;
      for (int k = 0; k < n; k++) if (bs[k] == b) seen = true;
      if (!seen && n < ${MAXB}) { bs[n] = b; n++; }
    } else if (byteAt(j, i, 0) < 255u) none = true;
  }
  if (n <= 1 && !none) { bw[0] = -1.0; return n; }
  for (int a = 1; a < n; a++) for (int b = a; b > 0 && bs[b - 1] > bs[b]; b--) { uint t = bs[b]; bs[b] = bs[b - 1]; bs[b - 1] = t; }
  float s = 0.0;
  for (int k = 0; k < n; k++) {
    bw[k] = bil4(tentOf(cj, ci, 0, bs[k]), tentOf(cj, ci, 1, bs[k]), tentOf(cj, ci, 2, bs[k]), tentOf(cj, ci, 3, bs[k]), tp);
    s += bw[k];
  }
  if (none) s += bil4(tentOf(cj, ci, 0, 0u), tentOf(cj, ci, 1, 0u), tentOf(cj, ci, 2, 0u), tentOf(cj, ci, 3, 0u), tp);
  float inv = s > 0.0 ? 1.0 / s : 0.0;
  for (int k = 0; k < n; k++) bw[k] *= inv;
  return n;
}
vec3 mixC(vec3 col, float w, vec3 c) { return col + w * (c - col); }
// _way_features at raster texel (i, k)
struct Feat { float sideOk; float dS; int code; float K; float dL; float dD; float fm; float phi; float hw; float t; };
float tAt(int ii, int kk) { return decG(bilB(taps(Xr(ii), Zr(kk)), 1)); }
// the way's half width at raster texel (i, k) from the cross gradient over the raster (np.gradient: one-sided at its edges)
float hwAt(int i, int k, float ts) {
  float gx = i == 0 ? (tAt(1, k) - ts) / uTexel : i == uPW.x - 1 ? (ts - tAt(i - 1, k)) / uTexel : (tAt(i + 1, k) - tAt(i - 1, k)) / (2.0 * uTexel);
  float gz = k == 0 ? (tAt(i, 1) - ts) / uTexel : k == uPW.y - 1 ? (ts - tAt(i, k - 1)) / uTexel : (tAt(i, k + 1) - tAt(i, k - 1)) / (2.0 * uTexel);
  return 1.0 / max(length(vec2(gx, gz)), 0.02);
}
Feat features(int i, int k, Tap tp, float ts) {
  Feat F;
  F.sideOk = byteAt(tp.j0, tp.i0, 3) > 0u && byteAt(tp.j0, tp.i1, 3) > 0u && byteAt(tp.j1, tp.i0, 3) > 0u && byteAt(tp.j1, tp.i1, 3) > 0u ? 1.0 : 0.0;
  F.dS = (bilB(tp, 3) - A_OFF) / A_SCALE;
  uint b0 = byteAt(tp.j0, tp.i0, 6);
  F.code = sameTaps(tp, 6) ? int(b0) & CMASK : 0;
  F.K = 2.0 * float(int(b0) >> CODE_BITS);
  float v4b = bilB(tp, 4), v5 = bilB(tp, 5), v7 = bilB(tp, 7);
  F.dL = (v4b - L_OFF) / L_SCALE;
  F.dD = (v5 - D_OFF) / D_SCALE;
  F.fm = (v4b - D_OFF) / D_SCALE;
  F.phi = npMod(atan(v7 - P_OFF, v5 - P_OFF) / (2.0 * PI) * F.K, max(F.K, 1.0));
  F.hw = hwAt(i, k, ts);
  F.t = ts;
  return F;
}
float treadKa(int bv) {
  for (int q = 0; q < uTKn; q++) if (int(uTKb[q / 4][q % 4]) == bv) return uTKa[q / 4][q % 4];
  return 1.0;
}
void stepsEdge(vec3 tr, out vec3 cn, out vec3 cr) {
  if (LM_ON == 0) { cn = ST_CN; cr = ST_CR; } else { cn = tr * LM_NK; cr = tr * LM_RK; }
}
// _way_colour_fine
vec3 colourFine(int bv, Feat F) {
  float tex = uTexel, hh = uWT0.w;
  int k = bv & KMASK, h = bv >> KIND_BITS;
  vec3 tr = WM[k].rgb, col = tr;
  if (h != 0 && WM[h].w > 0.0) {
    col = mixC(col, smooth01(F.dS, -hh, hh) * F.sideOk, WM[h].rgb);
    col = mixC(col, boxCov(F.dS, 0.0, KERB_M, tex) * F.sideOk, KERB_C);
  }
  if (F.code == C_CENTRE || F.code == C_LANE) col = mixC(col, boxCov(F.dL, -LINE_M / 2.0, LINE_M / 2.0, tex) * clamp(0.5 + F.dD / tex, 0.0, 1.0), LINES_C);
  if (F.code == C_SLEEPERS) {
    float x = (F.phi - roundEven(F.phi)) * BL_PITCH, ka = octKeep(BL_PITCH);
    float ca = ka * boxCov(x, -BL_W / 2.0, BL_W / 2.0, tex) + (1.0 - ka) * (BL_W / BL_PITCH);
    col = mixC(col, ca * boxCov(F.t * F.hw, -BL_LEN / 2.0, BL_LEN / 2.0, tex), BL_C);
  }
  if (F.code == C_TREADS) {
    float pitch = ST_PERIOD / max(F.K, 1.0), x = (F.phi - floor(F.phi)) * pitch;
    float rb = 0.5 * ST_RISER, ka = treadKa(bv);
    float wn = ka * (boxCov(x, 0.0, ST_NOS, tex) + boxCov(x - pitch, 0.0, ST_NOS, tex)) + (1.0 - ka) * (ST_NOS / pitch);
    float wr = ka * (boxCov(x, pitch - rb, pitch, tex) + boxCov(x + pitch, pitch - rb, pitch, tex)) + (1.0 - ka) * (rb / pitch);
    float wf = smooth01(F.fm, -hh, hh);
    vec3 cn, cr;
    stepsEdge(tr, cn, cr);
    col = mixC(col, wf * wn, cn);
    col = mixC(col, wf * wr, cr);
  }
  return col;
}
// coarse regime: the PATH-G bytes' area coverage under texel (i, k)
int coarseAcc(int i, int k, out int ab[${MAXA}], out float av[${MAXA}]) {
  ivec2 bx = blockOf(Xr(i), uWWin.z), bz = blockOf(Zr(k), uWWin.w);
  int n = 0;
  for (int cz = bz.x; cz < bz.y; cz++) for (int cx = bx.x; cx < bx.y; cx++) {
    int b = int(byteAt(cz, cx, 2));
    if (pathIdx(b & KMASK) < 0) continue;
    float cov = clamp(0.5 - decR(fB(cz, cx, 0)), 0.0, 1.0);
    if (!(cov > 0.0)) continue;
    int s = -1;
    for (int q = 0; q < n; q++) if (ab[q] == b) s = q;
    if (s < 0 && n < ${MAXA}) { s = n; ab[n] = b; av[n] = 0.0; n++; }
    if (s >= 0) av[s] += cov * shareOf(b);
  }
  for (int q = 0; q < n; q++) av[q] /= uTexel * uTexel;
  return n;
}
float accGet(int ab[${MAXA * 9}], float av[${MAXA * 9}], int nb[9], int t, int b) {
  for (int q = 0; q < nb[t]; q++) if (ab[t * ${MAXA} + q] == b) return av[t * ${MAXA} + q];
  return 0.0;
}
// a kind change ramps over one texel's [1 2 1] tent (texel <= 2 m), the texel's total kept
int coarseBlend(int i, int k, out int ob[${MAXA * 9}], out float ow[${MAXA * 9}]) {
  int ab[${MAXA}];
  float av[${MAXA}];
  int n = coarseAcc(i, k, ab, av);
  for (int q = 0; q < n; q++) { ob[q] = ab[q]; ow[q] = av[q]; }
  if (uTexel > KIND_BLEND_TEXEL) return n;
  int NB[${MAXA * 9}];
  float NV[${MAXA * 9}];
  int nn[9];
  for (int t = 0; t < 9; t++) {
    int ii = clamp(i + t % 3 - 1, 0, uPW.x - 1), kk = clamp(k + t / 3 - 1, 0, uPW.y - 1);
    int tb[${MAXA}];
    float tv[${MAXA}];
    nn[t] = coarseAcc(ii, kk, tb, tv);
    for (int q = 0; q < nn[t]; q++) { NB[t * ${MAXA} + q] = tb[q]; NV[t * ${MAXA} + q] = tv[q]; }
  }
  int m = 0;
  int ub[${MAXA * 9}];
  for (int t = 0; t < 9; t++) for (int q = 0; q < nn[t]; q++) {
    int b = NB[t * ${MAXA} + q];
    bool seen = false;
    for (int u = 0; u < m; u++) if (ub[u] == b) seen = true;
    if (!seen) { ub[m] = b; m++; }
  }
  if (m <= 1) return n;
  for (int a = 1; a < m; a++) for (int b = a; b > 0 && ub[b - 1] > ub[b]; b--) { int t = ub[b]; ub[b] = ub[b - 1]; ub[b - 1] = t; }
  float tot = 0.0, s = 0.0;
  for (int u = 0; u < m; u++) {
    float r[3];
    for (int c = 0; c < 3; c++) r[c] = (accGet(NB, NV, nn, c, ub[u]) + 2.0 * accGet(NB, NV, nn, 3 + c, ub[u]) + accGet(NB, NV, nn, 6 + c, ub[u])) * 0.25;
    ow[u] = (r[0] + 2.0 * r[1] + r[2]) * 0.25;
    ob[u] = ub[u];
    tot += accGet(NB, NV, nn, 4, ub[u]);
    s += ow[u];
  }
  float f = s > 0.0 ? tot / s : 0.0;
  for (int u = 0; u < m; u++) ow[u] *= f;
  return m;
}
// _way_cells: a built kind's mean albedo per 1 m cell
vec3 wayCell(int j, int i) {
  int B = int(byteAt(j, i, 2)), k = B & KMASK, h = pathIdx(k) >= 0 ? 0 : B >> KIND_BITS;
  vec3 col = WM[k].rgb;
  float A = fB(j, i, 3), side = A > 0.0 && h > 0 && WM[h].w > 0.0 ? 1.0 : 0.0, dS = (A - A_OFF) / A_SCALE;
  col = mixC(col, clamp(0.5 + dS, 0.0, 1.0) * side, WM[h].rgb);
  col = mixC(col, KERB_M * (abs(dS - KERB_M / 2.0) < 0.5 && side > 0.0 ? 1.0 : 0.0), KERB_C);
  int b6 = int(byteAt(j, i, 6)), code = b6 & CMASK;
  float v4b = fB(j, i, 4);
  if (code == C_CENTRE || code == C_LANE) {
    float dL = (v4b - L_OFF) / L_SCALE, dD = (fB(j, i, 5) - D_OFF) / D_SCALE;
    col = mixC(col, LINE_M * (abs(dL) < 0.5 ? 1.0 : 0.0) * clamp(0.5 + dD, 0.0, 1.0), LINES_C);
  }
  if (code == C_SLEEPERS) {
    int H1 = uWWin.w, W1 = uWWin.z;
    float gz = j == 0 ? decG(fB(1, i, 1)) - decG(fB(0, i, 1)) : j == H1 - 1 ? decG(fB(H1 - 1, i, 1)) - decG(fB(H1 - 2, i, 1)) : (decG(fB(j + 1, i, 1)) - decG(fB(j - 1, i, 1))) / 2.0;
    float gx = i == 0 ? decG(fB(j, 1, 1)) - decG(fB(j, 0, 1)) : i == W1 - 1 ? decG(fB(j, W1 - 1, 1)) - decG(fB(j, W1 - 2, 1)) : (decG(fB(j, i + 1, 1)) - decG(fB(j, i - 1, 1))) / 2.0;
    float o = decG(fB(j, i, 1)) / max(length(vec2(gx, gz)), 0.02);
    col = mixC(col, (BL_W / BL_PITCH) * clamp(BL_LEN / 2.0 - abs(o) + 0.5, 0.0, 1.0), BL_C);
  }
  if (code == C_TREADS) {
    float pitch = ST_PERIOD / max(2.0 * float(b6 >> CODE_BITS), 1.0);
    float fin = clamp(0.5 + (v4b - D_OFF) / D_SCALE, 0.0, 1.0);
    vec3 cn, cr;
    stepsEdge(WM[k].rgb, cn, cr);
    col = mixC(col, fin * ST_NOS / pitch, cn);
    col = mixC(col, fin * 0.5 * ST_RISER / pitch, cr);
  }
  return col;
}
// WAYS-Q4: [b0, b1) the 4 m cells whose centres lie in the texel's footprint (qBlock); one cell's plane
ivec2 qBlock(float c, int n) {
  return ivec2(clamp(int(ceil((c - uTexel / 2.0 - Q4_M / 2.0) / Q4_M)), 0, n), clamp(int(ceil((c + uTexel / 2.0 - Q4_M / 2.0) / Q4_M)), 0, n));
}
uvec4 q4At(int j, int i, int p) { return texelFetch(uQ4, ivec2(3 * (i - uQWin.x) + p, j - uQWin.y), 0); }
// the min dEdge (m) over the texel's 4 m cells (q4Dmin)
float q4Dmin(float x, float z) {
  ivec2 bx = qBlock(x, uQWin.z), bz = qBlock(z, uQWin.w);
  uint r = 255u;
  for (int cz = bz.x; cz < bz.y; cz++) for (int cx = bx.x; cx < bx.y; cx++) r = min(r, q4At(cz, cx, 2).x);
  return decR(float(r));
}
// q4Texel: the PATH-G bytes at their cross mean (the first 4 met), the built kinds at their flat look colour, every
// entry its area coverage; no sub-texel feature, no mark
vec3 q4Texel(int i, int k, vec3 rgb, vec4 hx, float fL) {
  float x = Xr(i), z = Zr(k);
  vec2 d = dOf(ivec2(i, k));
  ivec2 bx = qBlock(x, uQWin.z), bz = qBlock(z, uQWin.w);
  int pb[4];
  float pv[4];
  int np = 0;
  float aw = 0.0;
  vec3 ac = vec3(0.0), as3 = vec3(0.0);
  for (int cz = bz.x; cz < bz.y; cz++) for (int cx = bx.x; cx < bx.y; cx++) {
    uvec4 e = q4At(cz, cx, 0), cv = q4At(cz, cx, 1);
    for (int t = 0; t < 4; t++) {
      int id = int(e[t]);
      if (id == 0) continue;
      float c = float(cv[t]) * Q4_K;
      if (pathIdx(id & KMASK) >= 0) {
        int sl = -1;
        for (int u = 0; u < 4; u++) if (u < np && pb[u] == id) sl = u;
        if (sl < 0 && np < 4) { sl = np; pb[np] = id; pv[np] = 0.0; np++; }
        if (sl >= 0) pv[sl] += c * shareOf(id);
        continue;
      }
      if (!(WM[id].w > 0.0)) continue;
      aw += c;
      if (id == K_STEPS) as3 += c * WM[id].rgb; else ac += c * WM[id].rgb;
    }
  }
  vec3 o = rgb;
  float area = uTexel * uTexel;
  for (int u = 0; u < 4; u++) {
    if (u >= np) break;
    float w = pv[u] / area;
    if (w > 0.0) o = addPath(o, rgb, d, ivec2(i, k), pb[u], w, crossMeanOf(pb[u]), hx, 0.0);
  }
  return o + ac / area - (aw / area) * rgb + as3 / area * fL;
}
// _path_pass at raster texel (i, k): rgb before, hx = (hb, Hs rgb), fL
vec3 pathTexel(int i, int k, vec3 rgb, vec4 hx, float fL) {
  if (uQOn != 0) return q4Texel(i, k, rgb, hx, fL);
  float x = Xr(i), z = Zr(k);
  vec2 d = dOf(ivec2(i, k));
  vec3 o = rgb;
  if (uFine != 0) {
    Tap tp = taps(x, z);
    float dt = decR(bilB(tp, 0)), dd = dt;
    if (uWT0.z > 0.0) dd = dd - uWT0.z * (2.0 * gnoiseFx(7, d) - 1.0);
    float wt = 1.0 - smooth01(dd, uWT1.x, uWT1.y), wb = 1.0 - smooth01(dt, uWT1.z, uWT1.w);
    // the worn edge band (colour.worn): the host share of the edge ramp, fading out past it to uWN
    float ww = uWN > uWT1.y ? (1.0 - wt) * (1.0 - smooth01(dd, uWT1.y, uWN)) : 0.0;
    if (!(wt > 0.0) && !(wb > 0.0) && !(ww > 0.0)) return o;
    float ts = decG(bilB(tp, 1)), u = abs(ts);
    uint bs[${MAXB}];
    float bw[${MAXB}];
    int n = texelKinds(tp, bs, bw);
    bool fOk = false, hOk = false;
    float hw = 0.0;
    Feat F;
    for (int q = 0; q < n; q++) {
      int bv = int(bs[q]);
      float kb = bw[q];
      if (kb != -1.0 && !(kb > 0.0)) continue;
      if (pathIdx(bv & KMASK) >= 0) {
        float wk = ww > 0.0 ? PS[pathIdx(bv & KMASK)].w : 0.0;
        if (!(wt > 0.0) && !(wk > 0.0)) continue;
        if (!hOk) { hw = hwAt(i, k, ts); hOk = true; }
        vec4 cf = crossOf(bv, u, hw);
        if (uWT0.x < 1.0) cf = uWT0.x * cf + (1.0 - uWT0.x) * crossMeanOf(bv);
        float w = wt * shareOf(bv), nb = wk > 0.0 ? ww * shareOf(bv) * wk : 0.0;
        o = addPath(o, rgb, d, ivec2(i, k), bv, kb == -1.0 ? w : w * kb, cf, hx, kb == -1.0 ? nb : nb * kb);
      } else {
        if (!(wb > 0.0)) continue;
        if (!fOk) { F = features(i, k, tp, ts); fOk = true; }
        vec3 col = colourFine(bv, F);
        if ((bv & KMASK) == K_STEPS) col *= fL;
        float w = kb == -1.0 ? wb : wb * kb;
        o += w * (col - rgb);
      }
    }
    return o;
  }
  int ob[${MAXA * 9}];
  float ow[${MAXA * 9}];
  int n = coarseBlend(i, k, ob, ow);
  for (int q = 0; q < n; q++) if (ow[q] > 0.0) o = addPath(o, rgb, d, ivec2(i, k), ob[q], ow[q], crossMeanOf(ob[q]), hx, 0.0);
  ivec2 bx = blockOf(x, uWWin.z), bz = blockOf(z, uWWin.w);
  float aw = 0.0;
  vec3 ac = vec3(0.0), as3 = vec3(0.0);
  for (int cz = bz.x; cz < bz.y; cz++) for (int cx = bx.x; cx < bx.y; cx++) {
    int kk = int(byteAt(cz, cx, 2)) & KMASK;
    if (kk == 0 || pathIdx(kk) >= 0) continue;
    float cov = clamp(0.5 - decR(fB(cz, cx, 0)), 0.0, 1.0);
    if (!(cov > 0.0)) continue;
    vec3 cc = wayCell(cz, cx);
    aw += cov;
    if (kk == K_STEPS) as3 += cov * cc; else ac += cov * cc;
  }
  float area = uTexel * uTexel, fw = aw / area;
  return o + ac / area - fw * rgb + as3 / area * fL;
}
// _mark_fc at (xq, zq): (F, C) of out (1 - F) + C, ok false = no mark
vec4 markFc(float xq, float zq, float texel, out bool ok) {
  ok = false;
  Tap tp = taps(xq, zq);
  if (!sameTaps(tp, 6)) return vec4(0.0);
  int b0 = int(byteAt(tp.j0, tp.i0, 6)), code = b0 & CMASK;
  if (code < MARK0) return vec4(0.0);
  ok = true;
  int hi = b0 >> CODE_BITS;
  float v4b = bilB(tp, 4), v5 = bilB(tp, 5), v7 = bilB(tp, 7), tex = texel;
  float F = 0.0;
  vec3 C = vec3(0.0);
  float PZ = ZB_BAR + ZB_GAP, PX = GW_DASH + GW_GAP;
  float KS = max(2.0 * float(hi), 1.0), m0 = code == C_GRID ? GR_MARGIN : ZB_MARGIN, n = KS - 2.0 * m0 - 1.0;
  float phi = npMod(atan(v7 - P_OFF, v5 - P_OFF) / (2.0 * PI) * KS, KS), kf = floor(phi), fr = phi - kf;
  float rect = clamp(0.5 + (v4b - D_OFF) / D_SCALE / tex, 0.0, 1.0);
  if (code == C_ZEBRA || code == C_GRID) {
    float P = code == C_GRID ? GR_PITCH : PZ, bar = code == C_GRID ? GR_BAR : ZB_BAR;
    float x = fr * P, c0 = (P - bar) / 2.0, c1 = (P + bar) / 2.0;
    float sc = boxCov(phi * P, m0 * P, (m0 + n) * P, tex), bc = 0.0;
    for (int q = -1; q <= 1; q++) {
      float jj = float(q);
      if (kf + jj >= m0 && kf + jj <= m0 + n - 1.0) bc += boxCov(x - jj * P, c0, c1, tex);
    }
    float ka = code == C_GRID ? octKeep(P) : 1.0;
    bc = ka * bc + (1.0 - ka) * sc * (bar / P);
    if (code == C_ZEBRA) { F += bc * rect; C += bc * rect * ZB_C; }
    else { F += rect * sc; C += rect * ((sc - bc) * GR_PIT + bc * GR_C); }
  }
  if (code == C_XLINE) {
    float a = (v4b - L_OFF) / L_SCALE;
    float dn = min((v5 - D_OFF) / XO_SCALE, (v7 - D_OFF) / XO_SCALE);
    float inside = clamp(0.5 + dn / tex, 0.0, 1.0);
    if (hi == 1) {
      float w = boxCov(a, -GW_LINE / 2.0, GW_LINE / 2.0, tex) * inside;
      float x = npMod(max(dn, 0.0), PX);
      w *= boxCov(x, 0.0, GW_DASH, tex) + boxCov(x - PX, 0.0, GW_DASH, tex);
      F += w; C += w * GW_C;
    }
    if (hi == 2) {
      float w = boxCov(a, -SP_LINE / 2.0, SP_LINE / 2.0, tex) * inside;
      F += w; C += w * SP_C;
    }
    if (hi > TEETH_HB0) {
      float P = TRI_BASE + TRI_GAP, nt = max(float(hi - TEETH_HB0), 1.0);
      float u1 = GW_LINE / 2.0 + TRI_LGAP + TRI_LEN;
      float x = 0.5 * ((v5 - D_OFF) / XO_SCALE - (v7 - D_OFF) / XO_SCALE + nt * P - TRI_GAP);
      float kt = clamp(floor((x + TRI_GAP / 2.0) / P), 0.0, nt - 1.0);
      float cx = abs(x - kt * P - TRI_BASE / 2.0), hw = (TRI_BASE / 2.0) * clamp((u1 - a) / TRI_LEN, 0.0, 1.0);
      float w = boxCov(a, u1 - TRI_LEN, u1, tex) * clamp(0.5 + (hw - cx) / tex, 0.0, 1.0);
      F = F * (1.0 - w) + w;
      C = C * (1.0 - w) + w * TRI_C;
    }
  }
  return vec4(F, C);
}
vec3 markTexel(int i, int k, vec3 rgb) {
  if (uMarkOn == 0) return rgb;
  float F;
  vec3 C;
  if (uFine != 0) {
    bool ok;
    vec4 fc = markFc(Xr(i), Zr(k), uTexel, ok);
    if (!ok) return rgb;
    F = fc.x; C = fc.yzw;
  } else {
    ivec2 bx = blockOf(Xr(i), uWWin.z), bz = blockOf(Zr(k), uWWin.w);
    vec4 s = vec4(0.0);
    for (int cz = bz.x; cz < bz.y; cz++) for (int cx = bx.x; cx < bx.y; cx++) s += texelFetch(uWMk, ivec2(cx - uWWin.x, cz - uWWin.y), 0);
    float area = uTexel * uTexel;
    F = s.x / area; C = s.yzw / area;
  }
  return rgb * (1.0 - F) + C;
}
`;
}

// 2a. the host ring's masks x what they average (hostPass pass 1): 12 channels in 3 targets
const FS_WPREP = (ST) => HEAD + RASTER + NOISE + CLASSWIN + waysGLSL(ST) + `
uniform sampler2D uA;
uniform sampler2D uH;
uniform int uBare[${NCMAX}];  // bit 0 hc.bare, bit 1 hc.bare_above_treeline
uniform vec2 uTree;           // treeline, treeline_soft_m
layout(location = 0) out vec4 o0;
layout(location = 1) out vec4 o1;
layout(location = 2) out vec4 o2;
void main() {
  ivec2 q = ivec2(gl_FragCoord.xy);
  vec3 rgb = texelFetch(uA, q, 0).rgb;
  // the ring's distance: the texel centre's (1 m field), the texel's min over its 4 m cells + half a texel (WAYS-Q4)
  float dt = uQOn != 0 ? q4Dmin(Xr(q.x), Zr(q.y)) + uTexel / 2.0 : decR(bilB(taps(Xr(q.x), Zr(q.y)), 0));
  int bb = uBare[int(clsTexel(q) & 0x7fu)];
  float bare = (bb & 1) != 0 ? 1.0 : 0.0;
  if ((bb & 2) != 0) bare = max(bare, smooth01(texelFetch(uH, q, 0).x, uTree.x - uTree.y, uTree.x + uTree.y));
  float ring = dt >= H_RING + uTexel / 2.0 ? 1.0 : 0.0;
  o0 = vec4(ring, bare * ring, bare, rgb.r * ring);
  o1 = vec4(rgb.g * ring, rgb.b * ring, rgb.r, rgb.g);
  float rl = 0.0, L = 0.0;
  if (LM_ON != 0) { rl = dt >= LM_RING + uTexel / 2.0 ? 1.0 : 0.0; L = lum(rgb); }
  o2 = vec4(rgb.b, rl, L * rl, L);
}
`;
// 2b. the box sums along the row (hostPass pass 2, clipped to the raster): rh for the host's 9
// channels, rl for the stair luma's 3
// one prep target's sums a pass (uW 0, 1: P0 / P1 over the host radius; 2: P2.x over it, P2.yzw over
// the luma radius) — the same per-channel order as one MRT pass, a target freed after its own
const FS_WBOXH = HEAD + `
uniform sampler2D uP;
uniform ivec2 uPW;
uniform ivec2 uRad;
uniform int uW;
layout(location = 0) out vec4 o0;
void main() {
  ivec2 q = ivec2(gl_FragCoord.xy);
  vec4 s = vec4(0.0);
  if (uW < 2) {
    for (int k = max(0, q.x - uRad.x); k <= min(uPW.x - 1, q.x + uRad.x); k++) s += texelFetch(uP, ivec2(k, q.y), 0);
  } else {
    for (int k = max(0, q.x - uRad.x); k <= min(uPW.x - 1, q.x + uRad.x); k++) s.x += texelFetch(uP, ivec2(k, q.y), 0).x;
    if (uRad.y > 0) for (int k = max(0, q.x - uRad.y); k <= min(uPW.x - 1, q.x + uRad.y); k++) s.yzw += texelFetch(uP, ivec2(k, q.y), 0).yzw;
  }
  o0 = s;
}
`;
// 2c. the column sums -> the host (hb, Hs, fL), then _path_pass + _mark_pass at the texel
const FS_WPATH = (ST) => HEAD + RASTER + NOISE + CLASSWIN + waysGLSL(ST) + `
uniform sampler2D uA;
out vec4 o;
void main() {
  ivec2 q = ivec2(gl_FragCoord.xy);
  int rl = uRad.y;
  vec4 hx = hostAt(q);
  float fL = 1.0;
  if (LM_ON != 0) {
    int z0 = max(0, q.y - rl), z1 = min(uPW.y - 1, q.y + rl);
    vec3 l = vec3(0.0);
    for (int k = z0; k <= z1; k++) l += texelFetch(uS2, ivec2(q.x, k), 0).yzw;
    l /= float((z1 - z0 + 1) * (min(uPW.x - 1, q.x + rl) - max(0, q.x - rl) + 1));
    float Lg = l.x > 1e-3 ? l.y / l.x : l.z;
    float Lt = clamp(min(max(ST_L0, LM_K0 * Lg), LM_K1 * Lg), LM_FLOOR, LM_CEIL);
    fL = Lt / max(ST_L0, 1e-3);
  }
  vec3 rgb = texelFetch(uA, q, 0).rgb;
  o = vec4(markTexel(q.x, q.y, pathTexel(q.x, q.y, rgb, hx, fL)), 0.0);
}
`;

// 3. the apron (surfacegen _zone_albedo past the class map, the 6 m box of outside texels)
function apronGLSL() {
  const P = PAL.palette, b = (k) => v3(P[k].base);
  return HEAD + RASTER + NOISE + `
uniform sampler2D uA;
uniform sampler2D uH;
uniform ivec4 uInX;           // the in-world texels: i in [x, y), k in [z, w)
uniform int uRa;
uniform vec2 uBand[6];        // meadow, turf, turf_out, scree, rock, snow (m)
uniform float uJit;
out vec4 o;
vec3 zoneAlb(float elev, float pt, float mid) {
  float ez = elev + (pt - 0.5) * uJit, mot = 0.9 + 0.2 * mid;
  float w0 = 1.0 - smooth01(ez, uBand[0].x, uBand[0].y);
  float w1 = smooth01(ez, uBand[1].x, uBand[1].y) * (1.0 - smooth01(ez, uBand[2].x, uBand[2].y));
  float w2 = smooth01(ez, uBand[3].x, uBand[3].y), w3 = smooth01(ez, uBand[4].x, uBand[4].y), w4 = smooth01(ez, uBand[5].x, uBand[5].y) * 2.0;
  vec3 col = w0 * ${b('meadow')} * mot + w1 * ${b('turf')} * mot + w2 * ${b('scree')} * mot + w3 * ${b('rock')} * mot + w4 * ${b('snow')} * mot;
  return col / max(w0 + w1 + w2 + w3 + w4, 1e-6);
}
void main() {
  ivec2 q = ivec2(gl_FragCoord.xy);
  vec4 a = texelFetch(uA, q, 0);
  int k0 = max(0, q.y - uRa), k1 = min(uPW.y, q.y + uRa + 1), i0 = max(0, q.x - uRa), i1 = min(uPW.x, q.x + uRa + 1);
  float tot = float((k1 - k0) * (i1 - i0));
  float inz = float(max(0, min(k1, uInX.w) - max(k0, uInX.z))), inx = float(max(0, min(i1, uInX.y) - max(i0, uInX.x)));
  float wof = clamp((tot - inz * inx) / tot, 0.0, 1.0);
  if (!(wof > 0.0)) { o = a; return; }
  vec2 d = dOf(q);
  vec3 zc = zoneAlb(texelFetch(uH, q, 0).x, patchAt(d), midAt(d));
  o = vec4(a.rgb * (1.0 - wof) + zc * wof, 0.0);
}
`;
}

// 4. crowns. The discs: an instanced octagon per crown / shrub, its inradius the drawn radius + half
// a cell (every cell centre within R is covered; ground-paint-cost: the old square of R + 1.5 cells
// shaded 1.6x the fragments at a crown and ~3x at a shrub — the FS's own test decides, so the same keys);
// every cell centre inside: key = floor(zc 256) 16 + cls (zc relative to the integer yref),
// DEPTH32F GREATER (cleared 0) keeps the tallest, the R32F colour holds its key (cleared -1)
export const DISC_VERTS = 8, DISC_PAD = 0.5;      // (the inradius past R, cells: gate row 'disc octagon')
export const DISC_OCT = Array.from({ length: 8 }, (_, k) => { const a = ((k + 0.5) * Math.PI) / 4, s = 1 / Math.cos(Math.PI / 8); return [Math.cos(a) * s, Math.sin(a) * s]; });
const VS_DISC = HEAD + `
in vec4 aC;                   // x, z (grid-local m), R, cls
in vec3 aT;                   // top, base (- yref), dome
uniform vec4 uMap;            // (cell, 0, 2 / (nx cell), 2 / (nz cell))
flat out vec4 vC;
flat out vec3 vT;
void main() {
  // the strip's vertex -> the octagon's corner (0 1 7 2 6 3 5 4), corners at (k + 0.5) 45 deg
  const vec2 OCT[8] = vec2[8](${DISC_OCT.map(([x, y]) => `vec2(${fl(x)}, ${fl(y)})`).join(', ')});
  int id = gl_VertexID;
  vec2 c = OCT[(id & 1) == 1 ? (id + 1) / 2 : (8 - id / 2) % 8];
  vec2 p = aC.xy + c * (aC.z + ${fl(DISC_PAD)} * uMap.x);
  vC = aC;
  vT = aT;
  gl_Position = vec4(p * uMap.zw - 1.0, 0.0, 1.0);
}
`;
const FS_DISC = HEAD + `
uniform float uCell;
flat in vec4 vC;
flat in vec3 vT;
out vec4 o;
void main() {
  vec2 cc = (floor(gl_FragCoord.xy) + 0.5) * uCell;
  float q = length(cc - vC.xy) / max(vC.z, 1e-6);
  if (!(q < 1.0)) discard;
  float top = vT.x, base = vT.y;
  float zc = vT.z > 0.5 ? 0.5 * (top + base) + 0.5 * (top - base) * sqrt(1.0 - q * q) : top - (top - base) * q;
  float key = floor(zc * 256.0) * 16.0 + vC.w;
  o = vec4(key, 0.0, 0.0, 0.0);
  gl_FragDepth = (key + 1.0) / 16777216.0;
}
`;
const CROWN_C = () => `
const float CASTER_MIN = ${fl(LIGHT.CASTER_MIN_M)}, OBJ_MAX = ${fl(LIGHT.OBJ_MAX_M)}, OPEN_K = ${fl(LIGHT.OPEN_K)};
const float CAN0 = ${fl(LIGHT.CANOPY_M[0])}, CAN1 = ${fl(LIGHT.CANOPY_M[1])}, CAN_TOP = ${fl(LIGHT.CANOPY_TOP)};
const float OPAC[${OPACITY.length}] = ${arr('float', OPACITY.map(fl))};
const float SKY_M[3] = ${arr('float', LIGHT.SKY_FINE_M.map(fl))};
`;
// the crown grid's (g, obj, cls, Sf)
const FS_OBJ = HEAD + CROWN_C() + `
uniform sampler2D uK;
uniform sampler2D uG;
uniform float uYref;
out vec4 o;
void main() {
  ivec2 q = ivec2(gl_FragCoord.xy);
  float key = texelFetch(uK, q, 0).r, g = texelFetch(uG, q, 0).x, obj = 0.0, cls = 0.0;
  if (key >= 0.0) {
    float t = floor(key / 16.0), hgt = (t / 256.0 + uYref) - g;
    if (hgt >= CASTER_MIN) { obj = min(hgt, OBJ_MAX); cls = key - t * 16.0; }
  }
  o = vec4(g, obj, cls, obj + g);
}
`;
// the sun march (bake osteps: the most opaque crown on the ray) on the inner cells, 1 elsewhere
const FS_MARCH = HEAD + CROWN_C() + `
uniform sampler2D uCG;
uniform sampler2D uSteps;     // NS x 1 RGBA32F: (ox, oy, thr)
uniform int uNS;
uniform ivec4 uIn;            // ja, jb, la, lb
uniform ivec2 uN;
out vec4 o;
void main() {
  ivec2 q = ivec2(gl_FragCoord.xy);
  if (q.x < uIn.x || q.x >= uIn.y || q.y < uIn.z || q.y >= uIn.w) { o = vec4(1.0); return; }
  float z0 = texelFetch(uCG, q, 0).x, op = 0.0;
  for (int s = 0; s < uNS; s++) {
    vec3 st = texelFetch(uSteps, ivec2(s, 0), 0).xyz;
    ivec2 p = q + ivec2(st.xy);
    if (p.x < 0 || p.x >= uN.x || p.y < 0 || p.y >= uN.y) continue;
    vec4 c = texelFetch(uCG, p, 0);
    int cl = int(c.z);
    if (cl >= 2 && c.w - z0 > st.z && OPAC[cl] > op) op = OPAC[cl];
  }
  o = vec4(1.0 - op);
}
`;
// the sky box sums along the row: (obj, occupancy) at the three radii
const FS_SKYH = HEAD + `
uniform sampler2D uCG;
uniform ivec3 uSR;
uniform ivec2 uN;
uniform ivec2 uOff;           // the band target's (0, 0) cell in the grid
layout(location = 0) out vec4 o0;
layout(location = 1) out vec4 o1;
// (the radii nest, uSR ascending: ONE read a cell of the widest box feeds each sum whose box holds it — every
// sum still adds its own cells in ascending order, so the bytes of three loops; ground-paint-cost)
void main() {
  ivec2 q = ivec2(gl_FragCoord.xy) + uOff;
  vec2 s0 = vec2(0.0), s1 = vec2(0.0), s2 = vec2(0.0);
  for (int i = max(0, q.x - uSR.z); i <= min(uN.x - 1, q.x + uSR.z); i++) {
    float ob = texelFetch(uCG, ivec2(i, q.y), 0).y;
    vec2 v = vec2(ob, ob > 0.0 ? 1.0 : 0.0);
    int d = abs(i - q.x);
    if (d <= uSR.x) s0 += v;
    if (d <= uSR.y) s1 += v;
    s2 += v;
  }
  o0 = vec4(s0, s1);
  o1 = vec4(s2, 0.0, 0.0);
}
`;
// ... down the column -> ov; vo's 3 x 3 box on its own range; c; fsun / fsky (inner cells)
const FS_SKYV = HEAD + CROWN_C() + `
uniform sampler2D uCG;
uniform sampler2D uVo;
uniform sampler2D uH0;
uniform sampler2D uH1;
uniform ivec3 uSR;
uniform ivec2 uN;
uniform ivec4 uBox;           // ba, bb, ka2, kb2
uniform float uCellM;
uniform ivec2 uHOff;          // the row sums' band: its (0, 0) cell in the grid
out vec4 o;
void main() {
  ivec2 q = ivec2(gl_FragCoord.xy);
  float ov = 1.0;
  // the column sums of the three radii in one walk of the widest (FS_SKYH's nesting: each sum in its own order)
  vec2 sa[3] = vec2[3](vec2(0.0), vec2(0.0), vec2(0.0));
  for (int k = max(0, q.y - uSR.z); k <= min(uN.y - 1, q.y + uSR.z); k++) {
    ivec2 h = ivec2(q.x, k) - uHOff;
    int d = abs(k - q.y);
    if (d <= uSR.y) {
      vec4 h0 = texelFetch(uH0, h, 0);
      if (d <= uSR.x) sa[0] += h0.xy;
      sa[1] += h0.zw;
    }
    sa[2] += texelFetch(uH1, h, 0).xy;
  }
  for (int r = 0; r < 3; r++) {
    int R = uSR[r], k0 = max(0, q.y - R), k1 = min(uN.y - 1, q.y + R);
    vec2 a = sa[r];
    a /= float((k1 - k0 + 1) * (min(uN.x - 1, q.x + R) - max(0, q.x - R) + 1));
    float kr = OPEN_K * SKY_M[r], m2 = a.x * a.x, fk = a.y * kr;
    ov = min(ov, 1.0 - a.y * (m2 / (fk * fk + m2 + 1e-6)));
  }
  float vob = 0.0;
  if (q.x >= uBox.x && q.x < uBox.y && q.y >= uBox.z && q.y < uBox.w) {
    int k0 = max(0, q.y - 1), k1 = min(uN.y - 1, q.y + 1), i0 = max(0, q.x - 1), i1 = min(uN.x - 1, q.x + 1);
    for (int k = k0; k <= k1; k++) for (int i = i0; i <= i1; i++) vob += texelFetch(uVo, ivec2(i, k), 0).x;
    vob /= float((k1 - k0 + 1) * (i1 - i0 + 1));
  }
  vec4 cg = texelFetch(uCG, q, 0);
  float c = int(cg.z) >= 2 ? clamp((cg.y - CAN0) / (CAN1 - CAN0), 0.0, 1.0) * CAN_TOP : 0.0;
  o = vec4(vob + (1.0 - vob) * c, ov + (1.0 - ov) * c, 0.0, 0.0);
}
`;

// 5. the light: SHD2 + the crown factors -> E -> sRGB-8 -> relight; crop + row flip; the pool
// layer's level 0 and the scratch chain's (MRT)
const FS_LIGHT = HEAD + RASTER + CLASSWIN + `
uniform sampler2D uA;
uniform sampler2D uH;
uniform usampler2D uVis;
uniform usampler2D uSky;
uniform ivec4 uVisN;          // level w, h, window x0, z0 (cells)
uniform ivec4 uSkyN;
uniform vec2 uShdC;           // vis cell, sky cell
uniform sampler2D uFS;        // the crown grid's (fsun, fsky)
uniform vec4 uCG;             // slot x0 - grid X0, slot z0 - grid Z0, cell, on
uniform ivec2 uCGN;
uniform vec3 uSun;            // sx, sy, sz
uniform vec3 uLSky;
uniform vec3 uLGnd;
uniform vec3 uLSun;
uniform float uLW;
uniform int uWaterC;
uniform sampler2D uStr;       // RGBA32F the rock field's cells: (b / L, frac(b . cell centre / L)) (groundref strataField)
uniform ivec2 uStrN;          // its w, h
uniform vec4 uStrP;           // cell m, k (ground.strata; 0 = off for the slot), the cliff ramp's tan lo, hi
uniform uint uStrR;           // compact classes: the rock field's rock classes (bits)
uniform uint uStrC;           // ... its cliff classes (weighted by the slope ramp)
layout(location = 0) out vec4 o0;
layout(location = 1) out vec4 o1;
float s2l(float x) { return x <= 0.04045 ? x / 12.92 : pow((x + 0.055) / 1.055, 2.4); }
float l2s8(float x) {
  x = clamp(x, 0.0, 1.0);
  float s = x <= 0.0031308 ? x * 12.92 : 1.055 * pow(x, 1.0 / 2.4) - 0.055;
  return floor(s * 255.0 + 0.5);
}
float shd(usampler2D t, ivec4 N, float cell, vec2 w) {
  vec2 u = w / cell - 0.5, f = floor(u);
  int i0 = clamp(int(f.x), 0, N.x - 1), i1 = clamp(int(f.x) + 1, 0, N.x - 1), j0 = clamp(int(f.y), 0, N.y - 1), j1 = clamp(int(f.y) + 1, 0, N.y - 1);
  vec2 t2 = clamp(u - f, 0.0, 1.0), v = 1.0 - t2;
  float a = float(texelFetch(t, ivec2(i0 - N.z, j0 - N.w), 0).r) / 255.0, b = float(texelFetch(t, ivec2(i1 - N.z, j0 - N.w), 0).r) / 255.0;
  float c = float(texelFetch(t, ivec2(i0 - N.z, j1 - N.w), 0).r) / 255.0, d = float(texelFetch(t, ivec2(i1 - N.z, j1 - N.w), 0).r) / 255.0;
  return (a * v.x + b * t2.x) * v.y + (c * v.x + d * t2.x) * t2.y;
}
// groundref gridAt: bilinear (texel < cell) or the mean of the texel's n x n cells
vec2 gridAt(vec2 d) {
  if (uCG.w == 0.0) return vec2(1.0);
  vec2 p = uCG.xy + d;
  float cell = uCG.z;
  if (uTexel < cell) {
    vec2 u = p / cell - 0.5, f = floor(u);
    int i0 = clamp(int(f.x), 0, uCGN.x - 1), i1 = clamp(int(f.x) + 1, 0, uCGN.x - 1), j0 = clamp(int(f.y), 0, uCGN.y - 1), j1 = clamp(int(f.y) + 1, 0, uCGN.y - 1);
    vec2 t = clamp(u - f, 0.0, 1.0), v = 1.0 - t;
    vec2 a = texelFetch(uFS, ivec2(i0, j0), 0).xy, b = texelFetch(uFS, ivec2(i1, j0), 0).xy;
    vec2 c = texelFetch(uFS, ivec2(i0, j1), 0).xy, e = texelFetch(uFS, ivec2(i1, j1), 0).xy;
    return (a * v.x + b * t.x) * v.y + (c * v.x + e * t.x) * t.y;
  }
  int n = int(rnd(uTexel / cell)), i0 = int(rnd((p.x - uTexel / 2.0) / cell)), k0 = int(rnd((p.y - uTexel / 2.0) / cell));
  vec2 s = vec2(0.0);
  for (int k = 0; k < n; k++) for (int i = 0; i < n; i++) s += texelFetch(uFS, ivec2(clamp(i0 + i, 0, uCGN.x - 1), clamp(k0 + k, 0, uCGN.y - 1)), 0).xy;
  return s / float(n * n);
}
// groundref strataW / strataTilt: the strata class weight (bilinear of the 1 m indicator) and the tilt
float strInd(int cx, int cz, float ramp) {
  uint b = 1u << (clsAt(cx, cz) & 0x7fu);
  return (uStrR & b) != 0u ? 1.0 : (uStrC & b) != 0u ? ramp : 0.0;
}
float strW(vec2 w, float slope) {
  float ramp = smooth01(slope, uStrP.z, uStrP.w);
  vec2 u = w - 0.5, f = floor(u), t = u - f;
  int i = int(f.x), k = int(f.y);
  float a = strInd(i, k, ramp) * (1.0 - t.x) + strInd(i + 1, k, ramp) * t.x;
  float b = strInd(i, k + 1, ramp) * (1.0 - t.x) + strInd(i + 1, k + 1, ramp) * t.x;
  return a * (1.0 - t.y) + b * t.y;
}
vec3 strTilt(vec2 w, float h, vec2 g, vec3 n) {
  vec2 u = w / uStrP.x - 0.5, f = floor(u), t = clamp(u - f, 0.0, 1.0);
  vec3 P = vec3(0.0);
  for (int c = 0; c < 4; c++) {
    ivec2 ci = clamp(ivec2(f) + ivec2(c & 1, c >> 1), ivec2(0), uStrN - 1);
    vec4 F = texelFetch(uStr, ci, 0);
    if (F.xyz == vec3(0.0)) continue;
    float wc = ((c & 1) != 0 ? t.x : 1.0 - t.x) * ((c >> 1) != 0 ? t.y : 1.0 - t.y);
    if (wc <= 0.0) continue;
    vec2 gp = F.xz + F.y * g;
    float keep = octKeep(1.0 / max(length(gp), 1e-6));
    if (keep <= 0.0) continue;
    vec2 cc = (vec2(ci) + 0.5) * uStrP.x;
    float s = F.w + F.x * (w.x - cc.x) + F.y * h + F.z * (w.y - cc.y);
    vec3 b = normalize(F.xyz);
    P += wc * keep * sin(2.0 * PI * fract(s)) * (b - dot(b, n) * n);
  }
  return P;
}
void main() {
  ivec2 px = ivec2(gl_FragCoord.xy);
  ivec2 q = ivec2(px.x + uM, ${SLOT_PX - 1} - px.y + uM);
  vec2 d = dOf(q), w = vec2(uX0) + d;
  vec3 alb = clamp(texelFetch(uA, q, 0).rgb, 0.0, 255.0);
  vec4 H = texelFetch(uH, q, 0);
  float lam, ny;
  if (int(clsTexel(q) & 0x7fu) == uWaterC) { lam = uSun.z; ny = 1.0; } else {
    float n = sqrt(H.z * H.z + H.w * H.w + 1.0);
    lam = clamp((H.z * -uSun.x + H.w * uSun.y + uSun.z) / n, 0.0, 1.0);
    ny = 1.0 / n;
    float wr = uStrP.y > 0.0 ? strW(w, sqrt(H.z * H.z + H.w * H.w)) : 0.0;
    if (wr > 0.0) {
      vec3 nn = vec3(-H.z, 1.0, -H.w) / n, P = strTilt(w, H.x, H.zw, nn);
      float kw = uStrP.y * wr;
      if (kw * length(P) > 0.0) {
        vec3 n2 = normalize(nn + kw * P);
        lam = clamp(n2.x * uSun.x + n2.y * uSun.z - n2.z * uSun.y, 0.0, 1.0);
        ny = n2.y;
      }
    }
  }
  float vis = shd(uVis, uVisN, uShdC.x, w), sky = shd(uSky, uSkyN, uShdC.y, w);
  vec2 f = gridAt(d);
  float sun = lam * vis * f.x, sk = sky * f.y, wy = 0.5 * ny + 0.5;
  vec3 E = clamp(((wy * (uLSky - uLGnd) + uLGnd) * sk + sun * uLSun) * (1.0 / uLW), 0.0, 1.0);
  vec3 lit;
  for (int c = 0; c < 3; c++) {
    float e8 = l2s8(E[c]);
    lit[c] = l2s8(s2l(alb[c] / 255.0) * s2l(e8 / 255.0));
  }
  vec3 lin = vec3(s2l(lit.r / 255.0), s2l(lit.g / 255.0), s2l(lit.b / 255.0));
  o0 = vec4(lin, 1.0);
  o1 = o0;
}
`;
// the mips: level L from the scratch's L - 1 (BASE = MAX = L - 1), a 2 x 2 box in linear
const FS_MIP = HEAD + `
uniform sampler2D uS;
layout(location = 0) out vec4 o0;
layout(location = 1) out vec4 o1;
void main() {
  ivec2 p = ivec2(gl_FragCoord.xy) * 2;
  vec4 c = (texelFetch(uS, p, 0) + texelFetch(uS, p + ivec2(1, 0), 0) + texelFetch(uS, p + ivec2(0, 1), 0) + texelFetch(uS, p + ivec2(1, 1), 0)) * 0.25;
  o0 = c;
  o1 = c;
}
`;

// every program's (vertex, fragment) source, each made when asked (the warm-up makes one a task);
// ST = the static ways look (staticWays)
export function shaderMakers(ST) {
  return {
    tin: () => [VS_TIN, FS_TIN], rowfill: () => [VS_FULL, FS_ROWFILL], colfill: () => [VS_FULL, FS_COLFILL],
    albedo: () => [VS_FULL, FS_ALBEDO()], wprep: () => [VS_FULL, FS_WPREP(ST)], wboxh: () => [VS_FULL, FS_WBOXH],
    wpath: () => [VS_FULL, FS_WPATH(ST)], apron: () => [VS_FULL, apronGLSL()], disc: () => [VS_DISC, FS_DISC],
    obj: () => [VS_FULL, FS_OBJ], march: () => [VS_FULL, FS_MARCH], skyh: () => [VS_FULL, FS_SKYH],
    skyv: () => [VS_FULL, FS_SKYV], light: () => [VS_FULL, FS_LIGHT], mip: () => [VS_FULL, FS_MIP],
  };
}
export function shaderSources(ST) {
  return Object.fromEntries(Object.entries(shaderMakers(ST)).map(([nm, f]) => [nm, f()]));
}
// the GLSL chunks genomeGLSL / albedoGLSL sit on (FS_ALBEDO = HEAD + RASTER + NOISE + CLASSWIN + genome + tiling + albedo
// ({ tiling: true }) + main)
export const GLSL_CHUNKS = { HEAD, RASTER, NOISE, CLASSWIN };

// ---------------------------------------------------------------- the static look (ways: prepWays' constants)
export function staticWays(ids, noneId, treelineM, empty) {
  return staticWaysOf(ids, noneId, treelineM, empty, WAYSLOOK, MARKS_IN, PAL.palette);
}
// the fixed noise frames at a slot origin (surfacegen _gnoise's lattice, float64 split)
const PHI = 0.6180339887498949;
function frameOf(wl, seed, X0, Z0, M2 = null) {
  const a = (15.0 + 60.0 * ((seed * PHI) % 1.0)) * (Math.PI / 180.0), c = Math.cos(a), s = Math.sin(a);
  // (fx, fz) = A (x, z) + (seed 17.31, seed 9.77); A = rot(a) / wl [x M2]
  let A = [c / wl, -s / wl, s / wl, c / wl];
  if (M2) A = [A[0] * M2[0] + A[1] * M2[2], A[0] * M2[1] + A[1] * M2[3], A[2] * M2[0] + A[3] * M2[2], A[2] * M2[1] + A[3] * M2[3]];
  const fx = A[0] * X0 + A[1] * Z0 + seed * 17.31, fz = A[2] * X0 + A[3] * Z0 + seed * 9.77;
  const ix = Math.floor(fx), iz = Math.floor(fz);
  return { M: A, O: [ix, iz, fx - ix, fz - iz], sh: (seed * 40503) >>> 0 };
}
const FIXED = [[120, 5], [120, 6], [47, 11], [21, 112], [9.5, 23], [4.3, 124],
  [WAYSLOOK.noise.patch_wl_m, WAYSLOOK.noise.seed + 1], [WAYSLOOK.noise.edge_wl_m, WAYSLOOK.noise.seed]];
// NOISE's fixed frames at a slot origin (X0, Z0) -> {uFM, uFO, uFS} (exported: the ring painter)
export function fixedFrames(X0, Z0) {
  const uFM = new Float32Array(32), uFO = new Float32Array(32), uFS = new Uint32Array(8);
  FIXED.forEach(([wl, sd], f) => { const F = frameOf(wl, sd, X0, Z0); uFM.set(F.M, 4 * f); uFO.set(F.O, 4 * f); uFS[f] = F.sh; });
  return { uFM, uFO, uFS };
}
// the genome record table (genomeGLSL's uRec: RGBA32F, RW vec4s a row, one row per key) at texel `texel` and slot
// origin (X0, Z0): rows r0 .. r1 - 1 of keys written into rec (Float32Array RW * 4 * keys.length). gP: key -> genomeTexParams,
// gC: key -> Map(texel -> genomeTexelConsts) (else computed here), recs: genome.json classes. Exported: the ring painter
export function genomeRecRows(rec, keys, gP, gC, recs, texel, X0, Z0, r0 = 0, r1 = keys.length) {
  const M2 = [0, 0, 0, 0];
  for (let row = r0; row < r1; row++) {
    const k = keys[row];
    const Pm = gP.get(k), Cm = gC.get(k), C = (Cm && Cm.get(texel)) || genomeTexelConsts(Pm, texel), R = recs[k], o = row * RW * 4;
    const np = C.pal.length;
    if (np > PAL_MAX || Pm.cuts.length !== np - 1) throw new Error(`genome record ${k}: ${np} palette entries / ${Pm.cuts.length} cuts (max ${PAL_MAX})`);
    rec[o] = np;
    for (let i = 0; i < 17; i++) { rec[o + 4 + i] = Pm.zk[i]; rec[o + 24 + i] = Pm.yk[i]; }
    for (let i = 0; i < 8; i++) { rec[o + 44 + i] = C.al[i]; rec[o + 52 + i] = C.be[i]; }
    for (let i = 0; i < np - 1; i++) rec[o + 60 + i] = Pm.cuts[i];
    for (let e = 0; e < np; e++) rec.set(C.pal[e], o + 68 + 4 * e);
    const L0 = lab(R.mean_lin[0], R.mean_lin[1], R.mean_lin[2]), ed = R.elev.dlab_per_100m, sdl = R.slope.dlab_per_10deg;
    rec[o + 104] = L0[0]; rec[o + 105] = L0[1]; rec[o + 106] = L0[2]; rec[o + 107] = R.elev.ref_m;
    rec[o + 108] = ed[0]; rec[o + 109] = ed[1]; rec[o + 110] = ed[2]; rec[o + 111] = R.slope.ref_deg;
    rec[o + 112] = sdl[0]; rec[o + 113] = sdl[1]; rec[o + 114] = sdl[2]; rec[o + 115] = 0;
    M2[0] = Pm.sin / Pm.sqa; M2[1] = -Pm.cos / Pm.sqa; M2[2] = Pm.cos * Pm.sqa; M2[3] = Pm.sin * Pm.sqa;
    for (let oc = 0; oc < 8; oc++) {
      const sd = Pm.oseed[oc];
      if (!(Number.isInteger(sd) && Math.abs(sd) < 2 ** 24)) throw new Error(`genome record ${k}: octave seed ${sd}`);
      const F = frameOf(Pm.wl[oc], sd, X0, Z0, M2);
      rec.set(F.M, o + 116 + 4 * oc);
      rec.set(F.O, o + 148 + 4 * oc);
      rec[o + 180 + oc] = sd;
    }
  }
  return rec;
}

